import { useDispatch, useSelector } from 'react-redux'
import React, { useCallback, useState, useEffect, useRef, useMemo } from 'react'
import { DragDropContext, Draggable, Droppable } from 'react-beautiful-dnd'
import { createPortal } from 'react-dom'

import { Icon } from '@iconify/react'
import InfiniteScroll from 'react-infinite-scroll-component'

// antd
import {
    Typography,
    Button,
    Row,
    Col,
    theme,
    Input,
    Tooltip,
    Popover,
    Dropdown,
} from 'antd'

// slices
import {
    taskListSupport,
    taskCreateSupport,
    taskUpdateSupport,
    mainTaskList,
    mainTaskCreate,
    mainTaskUpdate,
    taskDeleteSupport,
    mainTaskDelete,
} from '../taskRefactored/tasks.slice'
import {
    buildMainTaskUpdateBodyFromTask,
    getTaskCreatedSuccessMessage,
    isTaskMutationSuccess,
    resolveReferenceId,
    MAIN_TASK_LIST_SORT,
} from '../taskRefactored/tasks.service'
import {
    createSwimlaneAction,
    changeSwimlanesPositionAction,
} from '../projects/projects.slice'
import { projectTaskListAssigneeUser } from '../users/users.slice'

// components
import CreateTaskCard from './components/CreateTaskCard'
import KanbanBoardSkeleton from './components/KanbanBoardSkeleton'
import DateRangePicker from './components/DateRangePicker'
import DeleteSectionModal from './components/DeleteSectionModal'
import ProjectTaskSectionMenu from './components/ProjectTaskSectionMenu'
import TaskSubTaskAccordion from './components/TaskSubTaskAccordion'
import { showActionNotification } from '../../components/notification'
import AssigneeSelectorButton from './components/AssigneeSelectorButton'
import EpicSelector, {
    getEpicDisplayOptionFromTask,
} from '../taskRefactored/components/epicSelector'
import TypeSelector from '../taskRefactored/components/typeSelector'
import TaskParentEpicModal from './components/TaskParentEpicModal'
import {
    confirmDeleteTask,
    getDeleteTaskMenuLabel,
} from './components/confirmDeleteTask'
import { useTaskListKindPermission } from './projectPermissions'
import unicornFxImage from '../../assets/images/task/unicorn.png'

// styles
import styles from './projectDetail.module.css'
import { getColumnColor } from '../../utils/commonUtils'
import { PRIVATE_ROUTES, buildProjectTaskTabSearch } from '../../routes'

const { Text } = Typography
const { TextArea } = Input

const TASK_TYPE_OPTIONS = [
    { label: 'Task', value: 'Task' },
    { label: 'Bug', value: 'Bug' },
]

const PENDING_TASK_PREFIX = 'pending-create:'

const isPendingCreateTask = (task) =>
    Boolean(
        task?.pendingCreate ||
        String(task?._id || '').startsWith(PENDING_TASK_PREFIX)
    )

const createPendingTaskId = () =>
    `${PENDING_TASK_PREFIX}${Date.now()}-${Math.random().toString(36).slice(2, 9)}`

const SECTION_NAME_MAX_DISPLAY_ON_HOVER = 20

/** Horizontal auto-scroll while dragging near board edges */
const BOARD_DRAG_SCROLL_EDGE_PX = 88
const BOARD_DRAG_SCROLL_MAX_SPEED = 32

const formatSectionNameDisplay = (
    name,
    maxLength = SECTION_NAME_MAX_DISPLAY_ON_HOVER
) => {
    const text = String(name ?? '')
    if (text.length <= maxLength) return text
    return `${text.slice(0, maxLength)}...`
}

const toSwimlanePosition = (value, fallback = 0) => {
    const n = Number(value)
    return Number.isFinite(n) ? n : fallback
}

const sortSwimlanesByPosition = (list) => {
    if (!Array.isArray(list)) return []
    return [...list].sort(
        (a, b) =>
            toSwimlanePosition(a?.position) - toSwimlanePosition(b?.position)
    )
}

const getSectionNameKey = (group) =>
    String(group?._id || group?.name || '').trim()

const getTaskEpicId = (task) => resolveReferenceId(task?.epic ?? task?.epicId)

/** `getColumnColor` always returns a 6-digit hex, so the splits below are safe. */
const hexToRgb = (hex) => [
    parseInt(hex.slice(1, 3), 16),
    parseInt(hex.slice(3, 5), 16),
    parseInt(hex.slice(5, 7), 16),
]

/**
 * The count chip in a column header reads as the column accent on a washed-out
 * version of the same hue, rather than white on the solid accent.
 */
const getColumnCountStyle = (sectionName) => {
    const accent = getColumnColor(sectionName)
    const [r, g, b] = hexToRgb(accent)
    return {
        color: accent,
        backgroundColor: `rgba(${r}, ${g}, ${b}, 0.16)`,
    }
}

/**
 * Subtasks are rendered inside their parent's accordion, never as their own
 * board card — so they must also be excluded when numbering drag indexes.
 */
const isBoardVisibleTask = (task) => !(task?.parentTask?.length > 0)

const getTaskAttachmentCount = (task) => {
    const listed =
        Number(task?.attachmentsLength) || Number(task?.attachmentLength) || 0
    if (listed > 0) return listed
    if (Array.isArray(task?.attachment)) return task.attachment.length
    if (Array.isArray(task?.attachments)) return task.attachments.length
    return 0
}

/**
 * Large projects return well over a thousand tasks in one column, and every
 * card carries several antd popovers plus a drag handle. Render a window of
 * them and grow it as the column is scrolled.
 */
const INITIAL_VISIBLE_CARDS = 20
const VISIBLE_CARDS_STEP = 20
/** Start revealing before the user actually reaches the bottom. */
const CARD_REVEAL_SCROLL_THRESHOLD_PX = 320

const collapsedColumnsStorageKey = (projectId, taskListKind) =>
    `pmx.kanban.collapsedColumns.${taskListKind || 'support'}.${projectId || 'none'}`

const readCollapsedColumnIds = (projectId, taskListKind) => {
    try {
        const raw = localStorage.getItem(
            collapsedColumnsStorageKey(projectId, taskListKind)
        )
        const parsed = raw ? JSON.parse(raw) : []
        return Array.isArray(parsed) ? parsed.map(String) : []
    } catch {
        return []
    }
}

const copyTaskContextLink = async (text) => {
    try {
        if (!text) return false
        if (navigator?.clipboard?.writeText) {
            await navigator.clipboard.writeText(text)
            return true
        }
    } catch {
        // fall through
    }
    try {
        const el = document.createElement('textarea')
        el.value = String(text)
        el.setAttribute('readonly', '')
        el.style.position = 'fixed'
        el.style.top = '-1000px'
        el.style.left = '-1000px'
        document.body.appendChild(el)
        el.select()
        const ok = document.execCommand('copy')
        document.body.removeChild(el)
        return ok
    } catch {
        return false
    }
}

const ProjectTaskList = ({
    projectTaskList,
    projectTaskListStatus,
    projectTaskListPage,
    projectTaskListIsNext,
    projectId,
    onTaskClick,
    taskDetailVisible,
    onAddTaskToFirstSwimlane,
    taskListKind = 'support',
    taskRouteView = 'board',
    liveTaskTitlePatch = null,
    onRefreshTasks,
    listQuery = null,
    showRefreshOverlay = false,
}) => {
    const dispatch = useDispatch()
    const isMain = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const swimlaneTypePayload = useMemo(
        () => (isMain ? { type: 'pms' } : {}),
        [isMain]
    )
    const { useToken } = theme
    const { token } = useToken()
    const currentProjectData = useSelector(
        (state) => state.projectsSlice?.currentProjectData
    )

    const [createTaskStates, setCreateTaskStates] = useState({})
    const [filteredAssignees] = useState([])
    const [isAddingSection, setIsAddingSection] = useState(false)
    const [newSectionName, setNewSectionName] = useState('')
    const [newSectionPlacement, setNewSectionPlacement] = useState(null)
    const [isCreatingSection, setIsCreatingSection] = useState(false)
    const sectionInputRef = useRef(null)
    const creatingSectionLockRef = useRef(false)
    const skipSectionBlurRef = useRef(false)
    const [localTaskList, setLocalTaskList] = useState(projectTaskList)
    const localTaskListRef = useRef(projectTaskList)
    const pendingCardReordersRef = useRef(0)
    const pendingColumnReordersRef = useRef(0)
    const [sectionMenuOpen, setSectionMenuOpen] = useState({})
    /** { [swimlaneId]: number } — how many cards that column currently renders. */
    const [visibleCardCounts, setVisibleCardCounts] = useState({})
    const [collapsedColumnIds, setCollapsedColumnIds] = useState(() =>
        readCollapsedColumnIds(projectId, taskListKind)
    )
    const [deleteModalOpen, setDeleteModalOpen] = useState(false)
    const [deleteSectionData, setDeleteSectionData] = useState(null)
    const [deleteOption, setDeleteOption] = useState('keep') // 'keep' or 'delete'
    const [deleteSectionLoading, setDeleteSectionLoading] = useState(false)
    const [editingSectionId, setEditingSectionId] = useState(null)
    const [editingSectionName, setEditingSectionName] = useState('')
    const sectionNameInputRef = useRef(null)
    const kanbanBoardScrollRef = useRef(null)
    const isBoardDraggingRef = useRef(false)
    const dragPointerXRef = useRef(0)
    const dragPointerReadyRef = useRef(false)
    const boardAutoScrollRafRef = useRef(null)
    const boardDragPointerCleanupRef = useRef(null)

    useEffect(() => {
        localTaskListRef.current = localTaskList
    }, [localTaskList])

    useEffect(() => {
        setCollapsedColumnIds(readCollapsedColumnIds(projectId, taskListKind))
    }, [projectId, taskListKind])

    useEffect(() => {
        if (!projectId) return
        try {
            localStorage.setItem(
                collapsedColumnsStorageKey(projectId, taskListKind),
                JSON.stringify(collapsedColumnIds)
            )
        } catch {
            // ignore storage errors
        }
    }, [collapsedColumnIds, projectId, taskListKind])

    const toggleColumnCollapsed = useCallback((columnId, collapsed) => {
        const id = String(columnId || '')
        if (!id) return
        setCollapsedColumnIds((prev) => {
            const next = new Set(prev)
            if (collapsed) next.add(id)
            else next.delete(id)
            return [...next]
        })
        if (collapsed) {
            setCreateTaskStates((prev) => {
                if (!prev[id]?.open) return prev
                return {
                    ...prev,
                    [id]: {
                        ...prev[id],
                        open: false,
                    },
                }
            })
        }
    }, [])

    useEffect(() => {
        return () => {
            if (boardAutoScrollRafRef.current != null) {
                cancelAnimationFrame(boardAutoScrollRafRef.current)
            }
            boardDragPointerCleanupRef.current?.()
        }
    }, [])

    const mapProjectTaskListToLocal = useCallback((list) => {
        if (!list) return list
        return list.map((group) => ({
            ...group,
            list: Array.isArray(group?.list)
                ? group.list.filter((task) => !isPendingCreateTask(task))
                : group?.list,
        }))
    }, [])

    // Sync localTaskList with projectTaskList (drops any stale pending-create placeholders)
    useEffect(() => {
        if (!projectTaskList) return
        if (
            pendingCardReordersRef.current > 0 ||
            pendingColumnReordersRef.current > 0
        ) {
            return
        }
        const mapped = mapProjectTaskListToLocal(projectTaskList)
        setLocalTaskList(mapped)
        localTaskListRef.current = mapped
    }, [projectTaskList, mapProjectTaskListToLocal])

    /**
     * Start over at the first window only for a genuinely new result set —
     * landing, filter or search. Resetting on every background refresh would
     * throw away the cards the user had already scrolled into view.
     */
    useEffect(() => {
        if (showRefreshOverlay) setVisibleCardCounts({})
    }, [showRefreshOverlay])

    useEffect(() => {
        if (!liveTaskTitlePatch?.taskId) return
        setLocalTaskList((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) return prev
            let changed = false
            const next = prev.map((group) => {
                if (!Array.isArray(group?.list) || group.list.length === 0) {
                    return group
                }
                const updatedList = group.list.map((task) => {
                    if (task?._id !== liveTaskTitlePatch.taskId) return task
                    changed = true
                    return {
                        ...task,
                        title: liveTaskTitlePatch.title,
                    }
                })
                return changed ? { ...group, list: updatedList } : group
            })
            return changed ? next : prev
        })
    }, [liveTaskTitlePatch])

    const refreshTasks = useCallback(
        (skip = 0) => {
            if (onRefreshTasks) {
                // Post-mutation sync: keep the board on screen, no skeleton.
                onRefreshTasks({}, { skip, silent: true })
                return
            }
            if (isMain) {
                dispatch(
                    mainTaskList({
                        projectId,
                        skip,
                        limit: 10,
                        sort: MAIN_TASK_LIST_SORT,
                    })
                )
            } else {
                dispatch(
                    taskListSupport({
                        skip,
                        limit: 10,
                        filter: { projectId },
                        listType: 'status',
                    })
                )
            }
        },
        [dispatch, isMain, onRefreshTasks, projectId]
    )

    const fetchMoreTasks = useCallback(() => {
        if (projectTaskListIsNext && projectTaskListStatus !== 'loading') {
            const nextPage = projectTaskListPage + 1
            const skip = (nextPage - 1) * 10

            if (onRefreshTasks) {
                // Pagination appends to the list — never blank it out.
                onRefreshTasks({}, { skip, silent: true })
                return
            }

            if (isMain) {
                dispatch(
                    mainTaskList({
                        projectId,
                        skip,
                        limit: 10,
                        sort: MAIN_TASK_LIST_SORT,
                    })
                )
            } else {
                dispatch(
                    taskListSupport({
                        skip: skip,
                        limit: 10,
                        filter: {
                            projectId: projectId,
                        },
                        listType: 'status',
                    })
                )
            }
        }
    }, [
        dispatch,
        projectTaskListIsNext,
        projectTaskListStatus,
        projectTaskListPage,
        projectId,
        isMain,
        onRefreshTasks,
    ])

    const handleAddSection = (sectionId = null, direction = null) => {
        skipSectionBlurRef.current = false
        creatingSectionLockRef.current = false
        setNewSectionPlacement(
            sectionId && direction ? { sectionId, direction } : null
        )
        if (sectionId) {
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
        }
        setIsAddingSection(true)
        setNewSectionName('')
        setTimeout(() => {
            sectionInputRef.current?.focus()
        }, 0)
    }

    const createSwimlane = async (sectionName) => {
        if (creatingSectionLockRef.current) return

        if (!sectionName || !sectionName.trim()) {
            setIsAddingSection(false)
            return
        }

        const trimmedSectionName = sectionName.trim()

        if (!projectId) {
            showActionNotification({
                type: 'error',
                message: 'Project ID is required',
            })
            setIsAddingSection(false)
            return
        }

        const existingSections =
            localTaskListRef.current || projectTaskList || []
        const isDuplicateSection = existingSections.some((group) => {
            return (
                getSectionNameKey(group).toLowerCase() ===
                trimmedSectionName.toLowerCase()
            )
        })

        if (isDuplicateSection) {
            showActionNotification({
                type: 'error',
                message: 'Section name already exists',
                subMessage: trimmedSectionName,
            })
            return
        }

        const sortedSections = sortSwimlanesByPosition(existingSections)
        const targetIndex = newSectionPlacement
            ? sortedSections.findIndex(
                  (group) =>
                      getSectionNameKey(group) ===
                      String(newSectionPlacement.sectionId)
              )
            : -1
        const insertionIndex =
            targetIndex >= 0
                ? targetIndex +
                  (newSectionPlacement.direction === 'right' ? 1 : 0)
                : sortedSections.length
        const newPosition = insertionIndex

        creatingSectionLockRef.current = true
        skipSectionBlurRef.current = true
        setIsAddingSection(false)
        setNewSectionName('')
        setNewSectionPlacement(null)
        setIsCreatingSection(true)

        const optimisticSection = {
            _id: trimmedSectionName,
            name: trimmedSectionName,
            position: newPosition,
            list: [],
        }

        try {
            setLocalTaskList((prev) => {
                const list = Array.isArray(prev) ? prev : []
                if (
                    list.some(
                        (group) =>
                            getSectionNameKey(group).toLowerCase() ===
                            trimmedSectionName.toLowerCase()
                    )
                ) {
                    return list
                }
                const next = sortSwimlanesByPosition(list).map((group) => ({
                    ...group,
                }))
                next.splice(insertionIndex, 0, optimisticSection)
                next.forEach((group, index) => {
                    group.position = index
                })
                localTaskListRef.current = next
                return next
            })

            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    name: trimmedSectionName,
                    position: String(newPosition),
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                showActionNotification({
                    type: 'success',
                    message: 'Section created successfully',
                    subMessage: trimmedSectionName,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-created',
                })
                refreshTasks(0)
            } else {
                setLocalTaskList((prev) =>
                    (Array.isArray(prev) ? prev : []).filter(
                        (group) =>
                            getSectionNameKey(group).toLowerCase() !==
                            trimmedSectionName.toLowerCase()
                    )
                )
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to create section',
                })
            }
        } catch (error) {
            setLocalTaskList((prev) =>
                (Array.isArray(prev) ? prev : []).filter(
                    (group) =>
                        getSectionNameKey(group).toLowerCase() !==
                        trimmedSectionName.toLowerCase()
                )
            )
            showActionNotification({
                type: 'error',
                message: 'Failed to create section',
            })
        } finally {
            creatingSectionLockRef.current = false
            setIsCreatingSection(false)
        }
    }

    const handleSectionInputBlur = () => {
        // Use setTimeout to allow click events to process first
        setTimeout(() => {
            if (skipSectionBlurRef.current) {
                skipSectionBlurRef.current = false
                return
            }
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            } else {
                setIsAddingSection(false)
                setNewSectionPlacement(null)
            }
        }, 200)
    }

    const handleSectionInputKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            skipSectionBlurRef.current = true
            if (newSectionName.trim()) {
                createSwimlane(newSectionName)
            } else {
                setIsAddingSection(false)
                setNewSectionPlacement(null)
            }
        } else if (e.key === 'Escape') {
            skipSectionBlurRef.current = true
            setIsAddingSection(false)
            setNewSectionName('')
            setNewSectionPlacement(null)
        }
    }

    const handleSectionMenuOpenChange = (sectionId, open) => {
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: open,
        }))
    }

    const handleRenameSection = (sectionId) => {
        // Find the section to get its current name
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )
        if (section) {
            setEditingSectionId(sectionId)
            setEditingSectionName(sectionId || 'Unassigned')
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            // Focus the input after state update
            setTimeout(() => {
                sectionNameInputRef.current?.focus()
                sectionNameInputRef.current?.select()
            }, 0)
        }
    }

    const handleSectionNameClick = (sectionId, e) => {
        if (!canEditModule) return
        // Don't trigger if clicking on the count or if already editing
        if (editingSectionId === sectionId) return
        e.stopPropagation()
        handleRenameSection(sectionId)
    }

    const handleSectionNameBlur = async (sectionId) => {
        if (!editingSectionId || editingSectionId !== sectionId) return

        const trimmedName = editingSectionName.trim()
        if (!trimmedName) {
            // Revert to original name if empty
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
            return
        }

        // Only update if name changed
        if (trimmedName !== sectionId) {
            try {
                setLocalTaskList((prev) =>
                    prev.map((group) =>
                        group?._id === sectionId
                            ? { ...group, _id: trimmedName }
                            : group
                    )
                )

                const result = await dispatch(
                    createSwimlaneAction({
                        id: projectId,
                        fieldName: 'update',
                        oldName: sectionId,
                        newName: trimmedName,
                        ...swimlaneTypePayload,
                    })
                )

                if (result?.payload?.data?.success) {
                    showActionNotification({
                        type: 'success',
                        message: 'Section renamed successfully',
                        subMessage: `${sectionId} → ${trimmedName}`,
                        actionButtonText: 'Undo',
                        notificationKey: 'section-renamed',
                    })

                    // Reset editing state
                    setEditingSectionId(null)
                    setEditingSectionName('')

                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to rename section',
                    })
                    // Revert to original name on error
                    setEditingSectionName(sectionId || 'Unassigned')
                    setEditingSectionId(null)
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to rename section',
                })
                // Revert to original name on error
                setEditingSectionName(sectionId || 'Unassigned')
            }
        }

        setEditingSectionId(null)
    }

    const handleSectionNameKeyDown = (sectionId, e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSectionNameBlur(sectionId)
        } else if (e.key === 'Escape') {
            // Revert to original name
            setEditingSectionName(sectionId || 'Unassigned')
            setEditingSectionId(null)
        }
    }

    const handleDeleteSection = (sectionId) => {
        // Find the section/group data
        const section = (localTaskList || projectTaskList).find(
            (group) => group?._id === sectionId
        )

        if (!section) {
            showActionNotification({
                type: 'error',
                message: 'Section not found',
            })
            setSectionMenuOpen((prev) => ({
                ...prev,
                [sectionId]: false,
            }))
            return
        }

        // Calculate task counts
        const tasks = section?.list || []

        setDeleteSectionData({
            sectionId,
            sectionName: sectionId || 'Unassigned',
            totalTasks: tasks.length,
            position: section?.position ?? 0,
        })
        setDeleteOption(tasks.length > 0 ? 'keep' : 'delete')
        setDeleteModalOpen(true)
        setSectionMenuOpen((prev) => ({
            ...prev,
            [sectionId]: false,
        }))
    }

    const handleConfirmDeleteSection = async () => {
        if (!deleteSectionData || deleteSectionLoading) return

        setDeleteSectionLoading(true)
        try {
            setLocalTaskList((prev) =>
                prev.filter(
                    (group) => group?._id !== deleteSectionData.sectionId
                )
            )

            const result = await dispatch(
                createSwimlaneAction({
                    id: projectId,
                    fieldName: 'remove',
                    name: deleteSectionData.sectionName,
                    position: deleteSectionData.position,
                    deleteTasks: deleteOption === 'delete',
                    ...swimlaneTypePayload,
                })
            )

            if (result?.payload?.data?.success) {
                const sectionName = deleteSectionData.sectionName || 'Section'
                const hadTasks = (deleteSectionData.totalTasks || 0) > 0
                const destinationSection =
                    (localTaskList || projectTaskList)?.find(
                        (group) =>
                            group?._id &&
                            group._id !== deleteSectionData.sectionId
                    )?._id || 'To Do'

                let subMessage = ''
                if (hadTasks && deleteOption === 'keep') {
                    subMessage = `All tasks/bugs have been moved to the "${destinationSection}" panel.`
                } else if (hadTasks && deleteOption === 'delete') {
                    subMessage =
                        'All tasks/bugs in this section have been deleted.'
                }

                showActionNotification({
                    type: 'success',
                    message: `${sectionName} Section deleted successfully.`,
                    subMessage,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
                setDeleteModalOpen(false)
                setDeleteSectionData(null)
                setDeleteOption('keep')

                refreshTasks(0)
            } else {
                showActionNotification({
                    message: 'Failed to delete section',
                    subMessage: result?.payload?.data?.message,
                    actionButtonText: 'Undo',
                    notificationKey: 'section-deleted',
                })
            }
        } catch (error) {
            showActionNotification({
                type: 'error',
                message: 'Failed to delete section',
            })
        } finally {
            setDeleteSectionLoading(false)
        }
    }

    const renderSectionMenu = (sectionId) => {
        const totalSections =
            (localTaskList || projectTaskList)?.filter(
                (group) => group?.list !== undefined
            )?.length || 0

        return (
            <ProjectTaskSectionMenu
                sectionId={sectionId}
                canDelete={canDeleteModule}
                isOnlySection={totalSections <= 1}
                onRename={handleRenameSection}
                onAdd={handleAddSection}
                onDelete={handleDeleteSection}
            />
        )
    }

    // Reorder swimlanes (columns) — optimistic local updates; API runs in background
    const orderColumns = useCallback(
        (initial, final) => {
            const currentList = localTaskListRef.current
            if (!currentList || currentList.length === 0) return
            if (initial === final) return

            const newTasks = sortSwimlanesByPosition(
                structuredClone(currentList)
            )
            if (
                initial < 0 ||
                final < 0 ||
                initial >= newTasks.length ||
                final >= newTasks.length
            ) {
                return
            }

            const initialPosition = toSwimlanePosition(
                newTasks[initial]?.position,
                initial
            )
            const finalPosition = toSwimlanePosition(
                newTasks[final]?.position,
                final
            )

            const [moved] = newTasks.splice(initial, 1)
            if (!moved) return
            newTasks.splice(final, 0, moved)
            newTasks.forEach((column, index) => {
                column.position = index
            })

            localTaskListRef.current = newTasks
            setLocalTaskList(newTasks)

            pendingColumnReordersRef.current += 1

            const persistReorder = async () => {
                try {
                    const result = await dispatch(
                        changeSwimlanesPositionAction({
                            initialPosition,
                            finalPosition,
                            projectId: projectId,
                            ...swimlaneTypePayload,
                        })
                    )

                    const data = result?.payload?.data
                    if (!data?.success) {
                        showActionNotification({
                            type: 'error',
                            message:
                                data?.message || 'Failed to reorder swimlanes',
                        })
                        refreshTasks(0)
                    }
                } catch (error) {
                    showActionNotification({
                        type: 'error',
                        message: 'Failed to reorder swimlanes',
                    })
                    refreshTasks(0)
                } finally {
                    pendingColumnReordersRef.current = Math.max(
                        0,
                        pendingColumnReordersRef.current - 1
                    )
                }
            }

            void persistReorder()
        },
        [dispatch, projectId, refreshTasks, swimlaneTypePayload]
    )

    // Reorder tasks (cards) — optimistic local updates; API runs in background
    const orderCards = useCallback(
        (source, destination, cardId) => {
            const currentList = localTaskListRef.current
            if (!currentList || currentList.length === 0) return

            const findCardSnapshot = (listState, id) => {
                for (const group of listState || []) {
                    const t = group?.list?.find((c) => c._id === id)
                    if (t) return { ...t }
                }
                return null
            }

            const cardSnapshot = findCardSnapshot(currentList, cardId)
            const newTasks = structuredClone(currentList)

            /**
             * react-beautiful-dnd reports indexes into the *rendered* card list,
             * which leaves out subtasks. Split each column the same way the board
             * renders it, move by array index, then renumber positions 0..n-1.
             */
            const splitColumn = (column) => {
                const list = column?.list || []
                return {
                    cards: list.filter(isBoardVisibleTask),
                    subTasks: list.filter((t) => !isBoardVisibleTask(t)),
                }
            }

            const commitColumn = (column, cards, subTasks) => {
                cards.forEach((card, index) => {
                    card.position = index
                })
                column.list = [...cards, ...subTasks]
            }

            if (source.droppableId === destination.droppableId) {
                const column = newTasks.find(
                    (x) => x?._id === source.droppableId
                )
                if (!column?.list) return

                const { cards, subTasks } = splitColumn(column)
                const [moved] = cards.splice(source.index, 1)
                if (!moved) return
                cards.splice(destination.index, 0, moved)
                commitColumn(column, cards, subTasks)
            } else {
                const sourceColumn = newTasks.find(
                    (x) => x?._id === source.droppableId
                )
                const destinationColumn = newTasks.find(
                    (x) => x?._id === destination.droppableId
                )
                if (!sourceColumn?.list || !destinationColumn) return

                const sourceSplit = splitColumn(sourceColumn)
                const destinationSplit = splitColumn(destinationColumn)

                const [moved] = sourceSplit.cards.splice(source.index, 1)
                if (!moved) return
                moved.status = destination.droppableId
                destinationSplit.cards.splice(destination.index, 0, moved)

                commitColumn(
                    sourceColumn,
                    sourceSplit.cards,
                    sourceSplit.subTasks
                )
                commitColumn(
                    destinationColumn,
                    destinationSplit.cards,
                    destinationSplit.subTasks
                )
            }

            localTaskListRef.current = newTasks
            setLocalTaskList(newTasks)

            pendingCardReordersRef.current += 1

            const persistReorder = async () => {
                try {
                    let data
                    if (isMain && cardSnapshot) {
                        const body = buildMainTaskUpdateBodyFromTask(
                            cardSnapshot,
                            projectId,
                            {
                                initialPosition: source?.index,
                                position: destination?.index,
                                status: destination?.droppableId,
                            }
                        )
                        const result = await dispatch(
                            mainTaskUpdate({ id: cardId, body })
                        )
                        data = result?.payload?.data
                    } else {
                        const result = await dispatch(
                            taskUpdateSupport({
                                _id: cardId,
                                updateTask: {
                                    initialPosition: source?.index,
                                    position: destination?.index,
                                    projectId: projectId,
                                    status: destination?.droppableId,
                                },
                            })
                        )
                        data = result?.payload?.data
                    }

                    const success = isMain
                        ? isTaskMutationSuccess(data)
                        : data?.success

                    if (!success) {
                        showActionNotification({
                            type: 'error',
                            message:
                                data?.message ||
                                (source.droppableId === destination.droppableId
                                    ? 'Failed to update task position'
                                    : 'Failed to move task'),
                        })
                        refreshTasks(0)
                    }
                } catch (error) {
                    showActionNotification({
                        type: 'error',
                        message:
                            source.droppableId === destination.droppableId
                                ? 'Failed to update task position'
                                : 'Failed to move task',
                    })
                    refreshTasks(0)
                } finally {
                    pendingCardReordersRef.current = Math.max(
                        0,
                        pendingCardReordersRef.current - 1
                    )
                }
            }

            void persistReorder()
        },
        [dispatch, projectId, isMain, refreshTasks]
    )

    const stopBoardDragAutoScroll = useCallback(() => {
        isBoardDraggingRef.current = false
        dragPointerReadyRef.current = false
        if (boardAutoScrollRafRef.current != null) {
            cancelAnimationFrame(boardAutoScrollRafRef.current)
            boardAutoScrollRafRef.current = null
        }
        boardDragPointerCleanupRef.current?.()
        boardDragPointerCleanupRef.current = null
    }, [])

    const tickBoardDragAutoScroll = useCallback(() => {
        const container = kanbanBoardScrollRef.current
        if (!container || !isBoardDraggingRef.current) {
            boardAutoScrollRafRef.current = null
            return
        }

        if (dragPointerReadyRef.current) {
            const rect = container.getBoundingClientRect()
            const x = dragPointerXRef.current
            const edge = BOARD_DRAG_SCROLL_EDGE_PX
            const maxSpeed = BOARD_DRAG_SCROLL_MAX_SPEED
            let deltaX = 0

            if (x < rect.left + edge) {
                const intensity = Math.min(1, (rect.left + edge - x) / edge)
                deltaX = -Math.ceil(maxSpeed * intensity)
            } else if (x > rect.right - edge) {
                const intensity = Math.min(1, (x - (rect.right - edge)) / edge)
                deltaX = Math.ceil(maxSpeed * intensity)
            }

            if (deltaX !== 0) {
                const maxScrollLeft =
                    container.scrollWidth - container.clientWidth
                container.scrollLeft = Math.max(
                    0,
                    Math.min(maxScrollLeft, container.scrollLeft + deltaX)
                )
            }
        }

        boardAutoScrollRafRef.current = requestAnimationFrame(
            tickBoardDragAutoScroll
        )
    }, [])

    const onDragStart = useCallback(() => {
        isBoardDraggingRef.current = true
        dragPointerReadyRef.current = false

        const updatePointerX = (event) => {
            const point = event?.touches?.[0] || event
            if (point?.clientX != null) {
                dragPointerXRef.current = point.clientX
                dragPointerReadyRef.current = true
            }
        }

        document.addEventListener('mousemove', updatePointerX)
        document.addEventListener('touchmove', updatePointerX, {
            passive: true,
        })
        boardDragPointerCleanupRef.current = () => {
            document.removeEventListener('mousemove', updatePointerX)
            document.removeEventListener('touchmove', updatePointerX)
        }

        if (boardAutoScrollRafRef.current == null) {
            boardAutoScrollRafRef.current = requestAnimationFrame(
                tickBoardDragAutoScroll
            )
        }
    }, [tickBoardDragAutoScroll])

    // Handle drag end
    const onDragEnd = useCallback(
        (draggable) => {
            stopBoardDragAutoScroll()
            const { draggableId } = draggable

            if (!canEditModule) {
                return
            }

            if (!draggable?.destination) {
                return
            }

            const source = draggable?.source
            const destination = draggable?.destination

            if (source !== null && destination !== null) {
                if (
                    source?.droppableId === destination?.droppableId &&
                    source?.index === destination?.index
                ) {
                    return
                }

                if (draggable?.type === 'cards') {
                    // Reordering swimlanes
                    orderColumns(source?.index, destination?.index)
                } else if (draggable?.type === 'column') {
                    // Moving tasks
                    orderCards(source, destination, draggableId)
                }
            }
        },
        [orderColumns, orderCards, canEditModule, stopBoardDragAutoScroll]
    )

    const initialCreateTask = React.useMemo(
        () => ({
            open: false,
            text: '',
            assigneeId: null,
            assigneeData: null,
            loading: false,
            _id: null,
            position: 'bottom', // 'top' or 'bottom'
            type: 'Task',
        }),
        []
    )

    const handleCreateTaskClick = (groupId, type = 'Task') => {
        if (!canEditModule) return
        setCreateTaskStates((prev) => ({
            ...prev,
            [groupId]: {
                ...initialCreateTask,
                open: true,
                _id: groupId,
                position: 'bottom',
                type: type === 'Bug' ? 'Bug' : 'Task',
            },
        }))
    }

    const handleCreateTaskClickFromHeader = useCallback(
        (groupId, type = 'Task') => {
            if (!canEditModule) return
            setCreateTaskStates((prev) => ({
                ...prev,
                [groupId]: {
                    ...initialCreateTask,
                    open: true,
                    _id: groupId,
                    position: 'top',
                    type: type === 'Bug' ? 'Bug' : 'Task',
                },
            }))
        },
        [initialCreateTask, canEditModule]
    )

    // Expose function to add task to first swimlane
    useEffect(() => {
        if (onAddTaskToFirstSwimlane) {
            onAddTaskToFirstSwimlane.current = (type = 'Task') => {
                if (!canEditModule) return
                const firstSwimlane = (localTaskList || projectTaskList)?.[0]
                if (firstSwimlane?._id) {
                    handleCreateTaskClickFromHeader(firstSwimlane._id, type)
                }
            }
        }
    }, [
        localTaskList,
        projectTaskList,
        onAddTaskToFirstSwimlane,
        handleCreateTaskClickFromHeader,
        canEditModule,
    ])

    const setCreateTask = useCallback(
        (groupId) => (newState) => {
            setCreateTaskStates((prev) => {
                const prevState = prev[groupId] || initialCreateTask
                const resolved =
                    typeof newState === 'function'
                        ? newState(prevState)
                        : newState
                return {
                    ...prev,
                    [groupId]: resolved,
                }
            })
        },
        [initialCreateTask]
    )

    const createTaskInFlightRef = useRef({})

    /** Grow a column's window as it is scrolled towards the bottom. */
    const handleColumnScroll = useCallback(
        (groupId, totalCards) => (event) => {
            const el = event.currentTarget
            const reachedRevealPoint =
                el.scrollTop + el.clientHeight >=
                el.scrollHeight - CARD_REVEAL_SCROLL_THRESHOLD_PX
            if (!reachedRevealPoint) return
            setVisibleCardCounts((prev) => {
                const current = prev[groupId] ?? INITIAL_VISIBLE_CARDS
                if (current >= totalCards) return prev
                return {
                    ...prev,
                    [groupId]: Math.min(
                        current + VISIBLE_CARDS_STEP,
                        totalCards
                    ),
                }
            })
        },
        []
    )

    /** Make sure a card appended to the bottom of a column is actually shown. */
    const revealColumnCards = useCallback((groupId, count) => {
        setVisibleCardCounts((prev) => {
            const current = prev[groupId] ?? INITIAL_VISIBLE_CARDS
            if (current >= count) return prev
            return { ...prev, [groupId]: count }
        })
    }, [])

    const normalizeAssigneeForTaskCard = (assigneeId, assigneeData) => {
        if (assigneeData && typeof assigneeData === 'object') {
            return [assigneeData]
        }
        if (assigneeId) {
            return [{ _id: assigneeId }]
        }
        return null
    }

    const buildPendingBoardTask = ({
        tempId,
        title,
        groupId,
        assigneeId,
        assigneeData,
        position,
        type = 'Task',
    }) => ({
        _id: tempId,
        title,
        status: groupId,
        position,
        type: type === 'Bug' ? 'Bug' : 'Task',
        assigneeId: normalizeAssigneeForTaskCard(assigneeId, assigneeData),
        pendingCreate: true,
    })

    const extractCreatedTaskFromResponse = (createdData) => {
        if (!createdData) return null
        const candidates = [
            createdData?.data?.task,
            createdData?.task,
            createdData?.result?.task,
            createdData?.data?.data,
            createdData?.result,
            createdData?.data,
            createdData,
        ]
        let fallback = null
        for (const item of candidates) {
            if (!item || typeof item !== 'object' || Array.isArray(item)) {
                continue
            }
            const id = item._id || item.id
            if (!id) continue
            const rawTitle = item.title ?? item.name
            const normalized = {
                ...item,
                _id: id,
                title: rawTitle != null ? String(rawTitle) : '',
            }
            if (
                rawTitle != null ||
                item.type != null ||
                item.status != null ||
                item.priority != null
            ) {
                return normalized
            }
            if (!fallback) fallback = normalized
        }
        return fallback
    }

    const insertTaskToGroup = (groups, groupId, task, placement = 'bottom') =>
        groups.map((group) => {
            if (group?._id !== groupId || !Array.isArray(group?.list)) {
                return group
            }
            const list =
                placement === 'top'
                    ? [task, ...group.list]
                    : [...group.list, task]
            return { ...group, list }
        })

    const replaceTaskInGroup = (groups, groupId, tempId, nextTask) =>
        groups.map((group) => {
            if (group?._id !== groupId || !Array.isArray(group?.list)) {
                return group
            }
            const idx = group.list.findIndex((t) => t._id === tempId)
            if (idx < 0) {
                return { ...group, list: [...group.list, nextTask] }
            }
            const list = group.list.map((t, i) => {
                if (i !== idx) return t
                const resolvedTitle =
                    String(nextTask?.title || '').trim() || t.title || ''
                return {
                    ...t,
                    ...nextTask,
                    title: resolvedTitle,
                    pendingCreate: false,
                }
            })
            return { ...group, list }
        })

    const removeTaskFromGroup = (groups, groupId, taskId) =>
        groups.map((group) =>
            group?._id === groupId && Array.isArray(group?.list)
                ? {
                      ...group,
                      list: group.list.filter((t) => t._id !== taskId),
                  }
                : group
        )

    const createTaskFromBoard = async (taskName, groupId, options = {}) => {
        if (!canEditModule) return
        if (createTaskInFlightRef.current[groupId]) {
            return
        }

        const createTaskState = createTaskStates[groupId]
        const assigneeId =
            options.assigneeId ??
            createTaskState?.assigneeId ??
            createTaskState?.assigneeData?._id ??
            null
        const assigneeData =
            options.assigneeData ?? createTaskState?.assigneeData ?? null
        const taskType =
            options.type === 'Bug' || createTaskState?.type === 'Bug'
                ? 'Bug'
                : 'Task'
        const trimmedTitle = String(taskName || '').trim()
        if (!trimmedTitle) {
            return
        }

        createTaskInFlightRef.current[groupId] = true
        const pendingTaskId = createPendingTaskId()

        setCreateTaskStates((prev) => ({
            ...prev,
            [groupId]: initialCreateTask,
        }))

        const taskPlacement =
            options.position ?? createTaskState?.position ?? 'bottom'
        const isTopPlacement = taskPlacement === 'top'

        if (!isTopPlacement) {
            // Appended below the current window — widen it, or the new card
            // would land off-screen in a large column.
            const currentGroup = (localTaskListRef.current || []).find(
                (g) => g?._id === groupId
            )
            revealColumnCards(
                groupId,
                (currentGroup?.list || []).filter(isBoardVisibleTask).length + 1
            )
        }

        setLocalTaskList((prev) => {
            const groups = prev || []
            const group = groups.find((g) => g?._id === groupId)
            const listLength = group?.list?.length ?? 0
            const boardPosition = isTopPlacement ? 0 : listLength
            const pendingTask = buildPendingBoardTask({
                tempId: pendingTaskId,
                title: trimmedTitle,
                groupId,
                assigneeId,
                assigneeData,
                position: boardPosition,
                type: taskType,
            })
            return insertTaskToGroup(
                groups,
                groupId,
                pendingTask,
                taskPlacement
            )
        })

        const payload = isMain
            ? {
                  title: trimmedTitle,
                  projectId: projectId,
                  description: '',
                  type: taskType,
                  status: groupId || 'To Do',
                  priority: 'Medium',
                  assigneeId,
                  start: createTaskState?.start || null,
                  end: createTaskState?.end || null,
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }
            : {
                  title: trimmedTitle,
                  projectId: projectId,
                  assigneeId,
                  type: taskType,
                  status: groupId || 'To Do',
                  ...(isTopPlacement ? { placement: 'top' } : {}),
              }

        try {
            const result = await dispatch(
                isMain ? mainTaskCreate(payload) : taskCreateSupport(payload)
            )

            const createdData = result?.payload?.data
            const createOk = isMain
                ? isTaskMutationSuccess(createdData)
                : createdData?.success

            if (createOk) {
                const createdTask = extractCreatedTaskFromResponse(createdData)
                if (createdTask) {
                    setLocalTaskList((prev) =>
                        replaceTaskInGroup(
                            prev || [],
                            groupId,
                            pendingTaskId,
                            createdTask
                        )
                    )
                }
                // If API omits task body, keep pending row until list refresh replaces local state

                showActionNotification({
                    type: 'success',
                    message: getTaskCreatedSuccessMessage(
                        createdTask?.type ||
                            payload?.type ||
                            options.type ||
                            'Task'
                    ),
                    subMessage: trimmedTitle,
                    actionButtonText: 'Undo',
                    notificationKey: 'task-add-success',
                })

                refreshTasks(0)
            } else {
                setLocalTaskList((prev) =>
                    removeTaskFromGroup(prev || [], groupId, pendingTaskId)
                )
                setCreateTaskStates((prev) => ({
                    ...prev,
                    [groupId]: {
                        open: true,
                        text: trimmedTitle,
                        assigneeId,
                        assigneeData,
                        loading: false,
                        _id: groupId,
                        position: createTaskState?.position || 'bottom',
                    },
                }))
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Could not create task',
                    notificationKey: 'task-add-error',
                })
            }
        } catch (error) {
            setLocalTaskList((prev) =>
                removeTaskFromGroup(prev || [], groupId, pendingTaskId)
            )
            setCreateTaskStates((prev) => ({
                ...prev,
                [groupId]: {
                    open: true,
                    text: trimmedTitle,
                    assigneeId,
                    assigneeData,
                    loading: false,
                    _id: groupId,
                    position: createTaskState?.position || 'bottom',
                },
            }))
            showActionNotification({
                type: 'error',
                message: 'Failed to create task',
            })
        } finally {
            delete createTaskInFlightRef.current[groupId]
        }
    }

    const renderContent = () => {
        // Skeleton while opening the tab, filtering or searching. Background
        // syncs after a task edit pass `silent`, so they leave the board alone.
        if (
            showRefreshOverlay ||
            (projectTaskListStatus === 'loading' &&
                projectTaskList?.length === 0)
        ) {
            return <KanbanBoardSkeleton />
        }

        if (
            projectTaskList?.length === 0 &&
            projectTaskListStatus === 'loaded'
        ) {
            return (
                <div className={styles.emptyState}>
                    <div className={styles.emptyIconBadge} aria-hidden>
                        <Icon
                            icon="mdi:clipboard-text-outline"
                            width={22}
                            height={22}
                            className={styles.emptyIcon}
                        />
                    </div>
                    <Text className={styles.emptyText}>No tasks yet</Text>
                    <Text className={styles.emptySubText}>
                        Create your first task to get started.
                    </Text>
                </div>
            )
        }

        if (Array.isArray(projectTaskList) && projectTaskList?.length > 0) {
            const isGrouped = projectTaskList[0]?.list

            if (isGrouped) {
                const renderNewSectionInputColumn = (key) => (
                    <Col key={key} className={styles.addSwimlaneCol}>
                        <Input
                            ref={sectionInputRef}
                            value={newSectionName}
                            onChange={(e) => setNewSectionName(e.target.value)}
                            onBlur={handleSectionInputBlur}
                            onKeyDown={handleSectionInputKeyDown}
                            placeholder="New section"
                            disabled={isCreatingSection}
                            className={styles.newSectionInput}
                            autoFocus
                        />
                    </Col>
                )

                return (
                    <DragDropContext
                        onDragStart={onDragStart}
                        onDragEnd={onDragEnd}
                    >
                        <div
                            ref={kanbanBoardScrollRef}
                            className={styles.kanbanBoardContainer}
                            style={{
                                width: '100%',
                                overflowX: 'auto',
                                overflowY: 'hidden',
                                scrollbarColor: '#a0a1a1 #fffbfb',
                                flex: '1 1 auto',
                                minHeight: 0,
                            }}
                        >
                            <Droppable
                                droppableId="swimlanes"
                                type="cards"
                                direction="horizontal"
                            >
                                {(provided) => (
                                    <Row
                                        wrap={false}
                                        gutter={14}
                                        className={styles.kanbanBoardRow}
                                        ref={provided.innerRef}
                                        {...provided.droppableProps}
                                    >
                                        {sortSwimlanesByPosition(
                                            localTaskList || projectTaskList
                                        ).map((group, index) => {
                                            const countTask =
                                                group?.list?.filter(
                                                    (item) => item !== null
                                                )?.length || 0
                                            // Drag indexes must be
                                            // contiguous over exactly the
                                            // cards we render.
                                            const columnCards = (
                                                group?.list || []
                                            ).filter(isBoardVisibleTask)
                                            const visibleCardCount =
                                                visibleCardCounts[group?._id] ??
                                                INITIAL_VISIBLE_CARDS
                                            // A prefix, so a rendered
                                            // card's index still matches
                                            // its index in columnCards.
                                            const visibleCards =
                                                columnCards.length >
                                                visibleCardCount
                                                    ? columnCards.slice(
                                                          0,
                                                          visibleCardCount
                                                      )
                                                    : columnCards
                                            const hiddenCardCount =
                                                columnCards.length -
                                                visibleCards.length
                                            const sectionName =
                                                group?._id || 'Unassigned'
                                            const displaySectionName =
                                                formatSectionNameDisplay(
                                                    sectionName,
                                                    SECTION_NAME_MAX_DISPLAY_ON_HOVER
                                                )
                                            const isSectionNameTruncated =
                                                displaySectionName !==
                                                sectionName
                                            // Visibility is driven by CSS
                                            // `:hover`; only the pinned
                                            // (menu open) state needs React.
                                            const isColumnMenuOpen = Boolean(
                                                sectionMenuOpen[group?._id]
                                            )
                                            const isCollapsed =
                                                collapsedColumnIds.includes(
                                                    String(group?._id)
                                                )
                                            const isPlacementTarget =
                                                isAddingSection &&
                                                String(
                                                    newSectionPlacement?.sectionId
                                                ) === String(group?._id)
                                            const showInputBefore =
                                                isPlacementTarget &&
                                                newSectionPlacement?.direction ===
                                                    'left'
                                            const showInputAfter =
                                                isPlacementTarget &&
                                                newSectionPlacement?.direction ===
                                                    'right'

                                            return (
                                                <React.Fragment
                                                    key={`section-${
                                                        group?._id ?? index
                                                    }`}
                                                >
                                                    {showInputBefore &&
                                                        renderNewSectionInputColumn(
                                                            `new-section-before-${
                                                                group?._id ??
                                                                index
                                                            }`
                                                        )}
                                                    <Draggable
                                                        draggableId={String(
                                                            group?._id ??
                                                                `section-${index}`
                                                        )}
                                                        index={index}
                                                        isDragDisabled={
                                                            !canEditModule
                                                        }
                                                    >
                                                        {(provided) => (
                                                            <Col
                                                                ref={
                                                                    provided.innerRef
                                                                }
                                                                {...provided.draggableProps}
                                                                className={`${
                                                                    styles.kanbanColumn
                                                                } ${
                                                                    isCollapsed
                                                                        ? styles.kanbanColumnCollapsed
                                                                        : ''
                                                                }`}
                                                                style={{
                                                                    ...provided
                                                                        .draggableProps
                                                                        .style,
                                                                }}
                                                            >
                                                                <div
                                                                    className={`${
                                                                        styles.swimlane
                                                                    } ${
                                                                        isCollapsed
                                                                            ? styles.swimlaneCollapsed
                                                                            : ''
                                                                    }`}
                                                                >
                                                                    {isCollapsed ? (
                                                                        <Droppable
                                                                            droppableId={String(
                                                                                group?._id ??
                                                                                    `section-${index}`
                                                                            )}
                                                                            type="column"
                                                                            direction="vertical"
                                                                        >
                                                                            {(
                                                                                dropProvided,
                                                                                dropSnapshot
                                                                            ) => (
                                                                                <Tooltip
                                                                                    title="Expand group"
                                                                                    placement="top"
                                                                                >
                                                                                    <div
                                                                                        ref={
                                                                                            dropProvided.innerRef
                                                                                        }
                                                                                        {...dropProvided.droppableProps}
                                                                                        {...provided.dragHandleProps}
                                                                                        className={`${
                                                                                            styles.collapsedLaneTab
                                                                                        } ${
                                                                                            dropSnapshot.isDraggingOver
                                                                                                ? styles.collapsedLaneTabOver
                                                                                                : ''
                                                                                        }`}
                                                                                        onClick={() =>
                                                                                            toggleColumnCollapsed(
                                                                                                group?._id,
                                                                                                false
                                                                                            )
                                                                                        }
                                                                                        role="button"
                                                                                        tabIndex={
                                                                                            0
                                                                                        }
                                                                                        onKeyDown={(
                                                                                            e
                                                                                        ) => {
                                                                                            if (
                                                                                                e.key ===
                                                                                                    'Enter' ||
                                                                                                e.key ===
                                                                                                    ' '
                                                                                            ) {
                                                                                                e.preventDefault()
                                                                                                toggleColumnCollapsed(
                                                                                                    group?._id,
                                                                                                    false
                                                                                                )
                                                                                            }
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            className={
                                                                                                styles.collapsedLaneLabel
                                                                                            }
                                                                                        >
                                                                                            <span
                                                                                                className={
                                                                                                    styles.collapsedLaneName
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    sectionName
                                                                                                }
                                                                                            </span>
                                                                                            <span
                                                                                                className={
                                                                                                    styles.collapsedLaneCount
                                                                                                }
                                                                                            >
                                                                                                {
                                                                                                    countTask
                                                                                                }
                                                                                            </span>
                                                                                        </span>
                                                                                        {
                                                                                            dropProvided.placeholder
                                                                                        }
                                                                                    </div>
                                                                                </Tooltip>
                                                                            )}
                                                                        </Droppable>
                                                                    ) : (
                                                                        <>
                                                                            <Row
                                                                                wrap={
                                                                                    false
                                                                                }
                                                                                align={
                                                                                    'middle'
                                                                                }
                                                                                justify={
                                                                                    'space-between'
                                                                                }
                                                                                className={
                                                                                    styles.columnHeader
                                                                                }
                                                                                style={{
                                                                                    position:
                                                                                        'relative',
                                                                                    padding:
                                                                                        editingSectionId ===
                                                                                        group?._id
                                                                                            ? '1.9px'
                                                                                            : '8px',
                                                                                }}
                                                                                {...provided.dragHandleProps}
                                                                            >
                                                                                <div
                                                                                    className={
                                                                                        styles.columnTitle
                                                                                    }
                                                                                >
                                                                                    {editingSectionId ===
                                                                                    group?._id ? (
                                                                                        <Input
                                                                                            ref={
                                                                                                sectionNameInputRef
                                                                                            }
                                                                                            value={
                                                                                                editingSectionName
                                                                                            }
                                                                                            onChange={(
                                                                                                e
                                                                                            ) =>
                                                                                                setEditingSectionName(
                                                                                                    e
                                                                                                        .target
                                                                                                        .value
                                                                                                )
                                                                                            }
                                                                                            onBlur={() =>
                                                                                                handleSectionNameBlur(
                                                                                                    group?._id
                                                                                                )
                                                                                            }
                                                                                            onKeyDown={(
                                                                                                e
                                                                                            ) =>
                                                                                                handleSectionNameKeyDown(
                                                                                                    group?._id,
                                                                                                    e
                                                                                                )
                                                                                            }
                                                                                            className={
                                                                                                styles.sectionNameInput
                                                                                            }
                                                                                            autoFocus
                                                                                            onClick={(
                                                                                                e
                                                                                            ) =>
                                                                                                e.stopPropagation()
                                                                                            }
                                                                                        />
                                                                                    ) : isSectionNameTruncated ? (
                                                                                        <Tooltip
                                                                                            title={
                                                                                                sectionName
                                                                                            }
                                                                                        >
                                                                                            <span
                                                                                                className={
                                                                                                    styles.columnSectionName
                                                                                                }
                                                                                                style={{
                                                                                                    fontWeight: 600,
                                                                                                    cursor: 'text',
                                                                                                }}
                                                                                                onClick={(
                                                                                                    e
                                                                                                ) => {
                                                                                                    handleSectionNameClick(
                                                                                                        group?._id,
                                                                                                        e
                                                                                                    )
                                                                                                }}
                                                                                            >
                                                                                                {
                                                                                                    displaySectionName
                                                                                                }
                                                                                            </span>
                                                                                        </Tooltip>
                                                                                    ) : (
                                                                                        <span
                                                                                            className={
                                                                                                styles.columnSectionName
                                                                                            }
                                                                                            style={{
                                                                                                fontWeight: 600,
                                                                                                cursor: 'text',
                                                                                            }}
                                                                                            onClick={(
                                                                                                e
                                                                                            ) => {
                                                                                                handleSectionNameClick(
                                                                                                    group?._id,
                                                                                                    e
                                                                                                )
                                                                                            }}
                                                                                        >
                                                                                            {
                                                                                                displaySectionName
                                                                                            }
                                                                                        </span>
                                                                                    )}
                                                                                    <Tooltip
                                                                                        title={`${countTask} ${
                                                                                            countTask ===
                                                                                            1
                                                                                                ? 'task'
                                                                                                : 'tasks'
                                                                                        } in this group`}
                                                                                        placement="top"
                                                                                        styles={{
                                                                                            body: {
                                                                                                fontSize: 12,
                                                                                                padding:
                                                                                                    '4px 8px',
                                                                                            },
                                                                                        }}
                                                                                    >
                                                                                        <span
                                                                                            className={
                                                                                                styles.columnCount
                                                                                            }
                                                                                            style={getColumnCountStyle(
                                                                                                sectionName
                                                                                            )}
                                                                                        >
                                                                                            {
                                                                                                countTask
                                                                                            }
                                                                                        </span>
                                                                                    </Tooltip>
                                                                                </div>
                                                                                <div
                                                                                    className={`${styles.columnHeaderActions}${
                                                                                        isColumnMenuOpen
                                                                                            ? ` ${styles.columnHeaderActionsOpen}`
                                                                                            : ''
                                                                                    }`}
                                                                                >
                                                                                    <Tooltip title="Collapse group">
                                                                                        <Icon
                                                                                            icon="mdi:arrow-collapse-left"
                                                                                            width={
                                                                                                16
                                                                                            }
                                                                                            height={
                                                                                                16
                                                                                            }
                                                                                            className={
                                                                                                styles.columnHeaderCollapseIcon
                                                                                            }
                                                                                            onClick={(
                                                                                                e
                                                                                            ) => {
                                                                                                e.stopPropagation()
                                                                                                toggleColumnCollapsed(
                                                                                                    group?._id,
                                                                                                    true
                                                                                                )
                                                                                            }}
                                                                                            style={{
                                                                                                cursor: 'pointer',
                                                                                                color: '#636363',
                                                                                            }}
                                                                                        />
                                                                                    </Tooltip>
                                                                                    {canEditModule ? (
                                                                                        <>
                                                                                            <Tooltip title="Add Task">
                                                                                                <Icon
                                                                                                    icon="mdi:plus"
                                                                                                    width={
                                                                                                        16
                                                                                                    }
                                                                                                    height={
                                                                                                        16
                                                                                                    }
                                                                                                    className={
                                                                                                        styles.columnHeaderPlusIcon
                                                                                                    }
                                                                                                    onClick={(
                                                                                                        e
                                                                                                    ) => {
                                                                                                        e.stopPropagation()
                                                                                                        handleCreateTaskClickFromHeader(
                                                                                                            group?._id
                                                                                                        )
                                                                                                    }}
                                                                                                    style={{
                                                                                                        cursor: 'pointer',
                                                                                                        color: '#636363',
                                                                                                    }}
                                                                                                />
                                                                                            </Tooltip>
                                                                                            <Popover
                                                                                                content={renderSectionMenu(
                                                                                                    group?._id
                                                                                                )}
                                                                                                trigger="click"
                                                                                                open={
                                                                                                    sectionMenuOpen[
                                                                                                        group
                                                                                                            ?._id
                                                                                                    ]
                                                                                                }
                                                                                                onOpenChange={(
                                                                                                    open
                                                                                                ) =>
                                                                                                    handleSectionMenuOpenChange(
                                                                                                        group?._id,
                                                                                                        open
                                                                                                    )
                                                                                                }
                                                                                                placement="bottomRight"
                                                                                                getPopupContainer={() =>
                                                                                                    document.body
                                                                                                }
                                                                                                classNames={{
                                                                                                    root: styles.sectionMenuPopover,
                                                                                                }}
                                                                                            >
                                                                                                <Tooltip title="More options">
                                                                                                    <Icon
                                                                                                        icon="weui:more-outlined"
                                                                                                        width={
                                                                                                            16
                                                                                                        }
                                                                                                        height={
                                                                                                            16
                                                                                                        }
                                                                                                        onClick={(
                                                                                                            e
                                                                                                        ) => {
                                                                                                            e.stopPropagation()
                                                                                                        }}
                                                                                                        style={{
                                                                                                            cursor: 'pointer',
                                                                                                            color: '#636363',
                                                                                                        }}
                                                                                                    />
                                                                                                </Tooltip>
                                                                                            </Popover>
                                                                                        </>
                                                                                    ) : null}
                                                                                </div>
                                                                            </Row>

                                                                            <Droppable
                                                                                droppableId={String(
                                                                                    group?._id ??
                                                                                        `section-${index}`
                                                                                )}
                                                                                type="column"
                                                                                direction="vertical"
                                                                            >
                                                                                {(
                                                                                    provided
                                                                                ) => (
                                                                                    <div
                                                                                        ref={
                                                                                            provided.innerRef
                                                                                        }
                                                                                        {...provided.droppableProps}
                                                                                        className={
                                                                                            styles.columnContent
                                                                                        }
                                                                                        id={`swimlane-${group?._id}`}
                                                                                        onScroll={handleColumnScroll(
                                                                                            group?._id,
                                                                                            columnCards.length
                                                                                        )}
                                                                                        style={{
                                                                                            // -20px for the swimlane's vertical padding.
                                                                                            maxHeight: `calc(100vh - 284px)`,
                                                                                            overflowY:
                                                                                                'auto',
                                                                                            scrollbarColor:
                                                                                                '#a0a1a1 #fffbfb',
                                                                                            scrollbarWidth:
                                                                                                'thin',
                                                                                        }}
                                                                                    >
                                                                                        {createTaskStates[
                                                                                            group
                                                                                                ?._id
                                                                                        ]
                                                                                            ?.open &&
                                                                                            createTaskStates[
                                                                                                group
                                                                                                    ?._id
                                                                                            ]
                                                                                                ?.position ===
                                                                                                'top' && (
                                                                                                <CreateTaskCard
                                                                                                    createTask={
                                                                                                        createTaskStates[
                                                                                                            group
                                                                                                                ?._id
                                                                                                        ]
                                                                                                    }
                                                                                                    setCreateTask={setCreateTask(
                                                                                                        group?._id
                                                                                                    )}
                                                                                                    filteredAssignees={
                                                                                                        filteredAssignees
                                                                                                    }
                                                                                                    createTaskFromBoard={(
                                                                                                        taskName,
                                                                                                        options
                                                                                                    ) =>
                                                                                                        createTaskFromBoard(
                                                                                                            taskName,
                                                                                                            group?._id,
                                                                                                            options
                                                                                                        )
                                                                                                    }
                                                                                                    initialCreateTask={
                                                                                                        initialCreateTask
                                                                                                    }
                                                                                                    projectId={
                                                                                                        projectId
                                                                                                    }
                                                                                                    status={
                                                                                                        group?._id
                                                                                                    }
                                                                                                    position={
                                                                                                        'top'
                                                                                                    }
                                                                                                    taskListKind={
                                                                                                        taskListKind
                                                                                                    }
                                                                                                    taskType={
                                                                                                        createTaskStates[
                                                                                                            group
                                                                                                                ?._id
                                                                                                        ]
                                                                                                            ?.type ||
                                                                                                        'Task'
                                                                                                    }
                                                                                                />
                                                                                            )}

                                                                                        {visibleCards.map(
                                                                                            (
                                                                                                task,
                                                                                                taskIndex
                                                                                            ) => {
                                                                                                return (
                                                                                                    <Draggable
                                                                                                        key={
                                                                                                            task._id ||
                                                                                                            taskIndex
                                                                                                        }
                                                                                                        draggableId={
                                                                                                            task._id
                                                                                                        }
                                                                                                        index={
                                                                                                            taskIndex
                                                                                                        }
                                                                                                        isDragDisabled={
                                                                                                            !canEditModule ||
                                                                                                            isPendingCreateTask(
                                                                                                                task
                                                                                                            )
                                                                                                        }
                                                                                                    >
                                                                                                        {(
                                                                                                            provided
                                                                                                        ) => (
                                                                                                            <div
                                                                                                                ref={
                                                                                                                    provided.innerRef
                                                                                                                }
                                                                                                                {...provided.draggableProps}
                                                                                                                {...(isPendingCreateTask(
                                                                                                                    task
                                                                                                                )
                                                                                                                    ? {}
                                                                                                                    : provided.dragHandleProps)}
                                                                                                            >
                                                                                                                <TaskCard
                                                                                                                    task={
                                                                                                                        task
                                                                                                                    }
                                                                                                                    token={
                                                                                                                        token
                                                                                                                    }
                                                                                                                    currentProjectData={
                                                                                                                        currentProjectData
                                                                                                                    }
                                                                                                                    onTaskClick={
                                                                                                                        onTaskClick
                                                                                                                    }
                                                                                                                    projectId={
                                                                                                                        projectId
                                                                                                                    }
                                                                                                                    dispatch={
                                                                                                                        dispatch
                                                                                                                    }
                                                                                                                    filteredAssignees={
                                                                                                                        filteredAssignees
                                                                                                                    }
                                                                                                                    setLocalTaskList={
                                                                                                                        setLocalTaskList
                                                                                                                    }
                                                                                                                    taskListKind={
                                                                                                                        taskListKind
                                                                                                                    }
                                                                                                                    refreshTasks={
                                                                                                                        refreshTasks
                                                                                                                    }
                                                                                                                    taskRouteView={
                                                                                                                        taskRouteView
                                                                                                                    }
                                                                                                                    listQuery={
                                                                                                                        listQuery
                                                                                                                    }
                                                                                                                />
                                                                                                            </div>
                                                                                                        )}
                                                                                                    </Draggable>
                                                                                                )
                                                                                            }
                                                                                        )}

                                                                                        {hiddenCardCount >
                                                                                            0 && (
                                                                                            <div
                                                                                                className={
                                                                                                    styles.columnMoreCards
                                                                                                }
                                                                                            >
                                                                                                <Icon
                                                                                                    icon="line-md:loading-loop"
                                                                                                    width={
                                                                                                        16
                                                                                                    }
                                                                                                    height={
                                                                                                        16
                                                                                                    }
                                                                                                />
                                                                                                <span>
                                                                                                    {
                                                                                                        hiddenCardCount
                                                                                                    }{' '}
                                                                                                    more
                                                                                                </span>
                                                                                            </div>
                                                                                        )}

                                                                                        {createTaskStates[
                                                                                            group
                                                                                                ?._id
                                                                                        ]
                                                                                            ?.open &&
                                                                                        createTaskStates[
                                                                                            group
                                                                                                ?._id
                                                                                        ]
                                                                                            ?.position ===
                                                                                            'bottom' ? (
                                                                                            <CreateTaskCard
                                                                                                createTask={
                                                                                                    createTaskStates[
                                                                                                        group
                                                                                                            ?._id
                                                                                                    ]
                                                                                                }
                                                                                                setCreateTask={setCreateTask(
                                                                                                    group?._id
                                                                                                )}
                                                                                                filteredAssignees={
                                                                                                    filteredAssignees
                                                                                                }
                                                                                                createTaskFromBoard={(
                                                                                                    taskName,
                                                                                                    options
                                                                                                ) =>
                                                                                                    createTaskFromBoard(
                                                                                                        taskName,
                                                                                                        group?._id,
                                                                                                        options
                                                                                                    )
                                                                                                }
                                                                                                initialCreateTask={
                                                                                                    initialCreateTask
                                                                                                }
                                                                                                projectId={
                                                                                                    projectId
                                                                                                }
                                                                                                status={
                                                                                                    group?._id
                                                                                                }
                                                                                                taskListKind={
                                                                                                    taskListKind
                                                                                                }
                                                                                                taskType={
                                                                                                    createTaskStates[
                                                                                                        group
                                                                                                            ?._id
                                                                                                    ]
                                                                                                        ?.type ||
                                                                                                    'Task'
                                                                                                }
                                                                                            />
                                                                                        ) : null}
                                                                                        {
                                                                                            provided.placeholder
                                                                                        }
                                                                                        {canEditModule &&
                                                                                            !createTaskStates[
                                                                                                group
                                                                                                    ?._id
                                                                                            ]
                                                                                                ?.open && (
                                                                                                <div
                                                                                                    style={{
                                                                                                        marginTop:
                                                                                                            '8px',
                                                                                                    }}
                                                                                                >
                                                                                                    <Button
                                                                                                        type="text"
                                                                                                        className={
                                                                                                            styles.createTaskButton
                                                                                                        }
                                                                                                        onClick={() =>
                                                                                                            handleCreateTaskClick(
                                                                                                                group?._id
                                                                                                            )
                                                                                                        }
                                                                                                        icon={
                                                                                                            <Icon
                                                                                                                icon="tabler:plus"
                                                                                                                width={
                                                                                                                    18
                                                                                                                }
                                                                                                                height={
                                                                                                                    18
                                                                                                                }
                                                                                                            />
                                                                                                        }
                                                                                                    >
                                                                                                        Add
                                                                                                        Task
                                                                                                    </Button>
                                                                                                </div>
                                                                                            )}
                                                                                    </div>
                                                                                )}
                                                                            </Droppable>
                                                                        </>
                                                                    )}
                                                                </div>
                                                            </Col>
                                                        )}
                                                    </Draggable>
                                                    {showInputAfter &&
                                                        renderNewSectionInputColumn(
                                                            `new-section-after-${
                                                                group?._id ??
                                                                index
                                                            }`
                                                        )}
                                                </React.Fragment>
                                            )
                                        })}
                                        {canEditModule && (
                                            <Col
                                                className={
                                                    styles.addSwimlaneCol
                                                }
                                            >
                                                {isAddingSection &&
                                                !newSectionPlacement ? (
                                                    <Input
                                                        ref={sectionInputRef}
                                                        value={newSectionName}
                                                        onChange={(e) =>
                                                            setNewSectionName(
                                                                e.target.value
                                                            )
                                                        }
                                                        onBlur={
                                                            handleSectionInputBlur
                                                        }
                                                        onKeyDown={
                                                            handleSectionInputKeyDown
                                                        }
                                                        placeholder="New section"
                                                        disabled={
                                                            isCreatingSection
                                                        }
                                                        className={
                                                            styles.newSectionInput
                                                        }
                                                        autoFocus
                                                    />
                                                ) : (
                                                    <Button
                                                        type="text"
                                                        className={
                                                            styles.addSectionButton
                                                        }
                                                        onClick={() =>
                                                            handleAddSection()
                                                        }
                                                        icon={
                                                            <Icon
                                                                icon="mdi:plus"
                                                                style={{
                                                                    fontSize: 18,
                                                                }}
                                                            />
                                                        }
                                                    >
                                                        Add section
                                                    </Button>
                                                )}
                                            </Col>
                                        )}
                                        {provided.placeholder}
                                    </Row>
                                )}
                            </Droppable>
                        </div>
                    </DragDropContext>
                )
            } else {
                return (
                    <InfiniteScroll
                        dataLength={projectTaskList?.length}
                        next={fetchMoreTasks}
                        hasMore={projectTaskListIsNext}
                        loader={
                            <div className={styles.loadingMore}>
                                <Icon
                                    icon="line-md:loading-loop"
                                    style={{ fontSize: 24, color: '#6366f1' }}
                                />
                            </div>
                        }
                        scrollableTarget="project-detail-scroll"
                    >
                        <div className={styles.taskList}>
                            {projectTaskList.map((task) => (
                                <TaskCard
                                    key={task._id}
                                    task={task}
                                    token={token}
                                    currentProjectData={currentProjectData}
                                    onTaskClick={onTaskClick}
                                    projectId={projectId}
                                    dispatch={dispatch}
                                    filteredAssignees={filteredAssignees}
                                    setLocalTaskList={setLocalTaskList}
                                    taskListKind={taskListKind}
                                    refreshTasks={refreshTasks}
                                    taskRouteView={taskRouteView}
                                    listQuery={listQuery}
                                />
                            ))}
                        </div>
                    </InfiniteScroll>
                )
            }
        }

        return null
    }

    const handleModalCancel = () => {
        if (deleteSectionLoading) return
        setDeleteModalOpen(false)
        setDeleteSectionData(null)
        setDeleteOption('keep')
    }

    return (
        <>
            {renderContent()}
            <DeleteSectionModal
                open={deleteModalOpen}
                onCancel={handleModalCancel}
                onConfirm={handleConfirmDeleteSection}
                deleteSectionData={deleteSectionData}
                deleteOption={deleteOption}
                setDeleteOption={setDeleteOption}
                confirmLoading={deleteSectionLoading}
            />
        </>
    )
}

// Task Card Component matching the new design
const TaskCardComponent = ({
    setLocalTaskList,
    task,
    token,
    currentProjectData,
    onTaskClick,
    projectId,
    dispatch,
    filteredAssignees,
    taskListKind = 'support',
    refreshTasks,
    containerClassName = '',
    readOnlyActions: readOnlyActionsProp = false,
    singleLineTitle = false,
    taskRouteView = 'board',
    listQuery = null,
}) => {
    const isMainCard = taskListKind === 'main'
    const { canEdit: canEditModule, canDelete: canDeleteModule } =
        useTaskListKindPermission(taskListKind)
    const readOnlyActions = readOnlyActionsProp || !canEditModule
    const isPendingCreate = isPendingCreateTask(task)
    const isCardInteractive = !readOnlyActions && !isPendingCreate
    const attachmentCount = getTaskAttachmentCount(task)
    const taskTypeLabel = useMemo(() => {
        const normalized = String(task?.type || 'Task').trim()
        return normalized || 'Task'
    }, [task?.type])
    const isTypeDisabledForIssuesSprint = useMemo(() => {
        if (!isMainCard) return false
        const sprintLabel = task?.sprint?.title ?? task?.sprintId?.title ?? ''
        return /^issues$/i.test(String(sprintLabel).trim())
    }, [isMainCard, task?.sprint, task?.sprintId])
    const taskEpicId = useMemo(() => getTaskEpicId(task), [task])
    const taskEpicDisplayOption = useMemo(
        () => getEpicDisplayOptionFromTask(task),
        [task]
    )
    const showTaskEpic =
        isMainCard && taskRouteView !== 'sprint' && Boolean(taskEpicId)
    const showTypeInMetadataRow = isMainCard
    const hasTaskKey = Boolean(task?.taskKey)
    const showKeyInMetadataRow = hasTaskKey
    const isTaskCompleted = Boolean(task?.isCompleted ?? task?.isComplete)
    const selectedAssigneeForDisplay = Array.isArray(task?.assigneeId)
        ? (task?.assigneeId?.[0] ?? null)
        : (task?.assigneeId ?? null)

    const taskDeepLink = useMemo(() => {
        if (!projectId || !task?._id) return ''
        const path = PRIVATE_ROUTES.projects.projectIdViewTask(
            projectId,
            taskRouteView,
            task._id
        )
        const search =
            taskListKind === 'main'
                ? buildProjectTaskTabSearch('development')
                : ''
        return `${window.location.origin}${path}${search}`
    }, [projectId, task?._id, taskRouteView, taskListKind])

    const hasParentTask = Boolean(
        task?.parentTask &&
        (Array.isArray(task.parentTask)
            ? task.parentTask.length > 0
            : Object.keys(task.parentTask || {}).length > 0)
    )
    const canAddSubtasks = !/^bug$/i.test(String(taskTypeLabel).trim())
    const [isEditing, setIsEditing] = useState(false)
    const [editedTitle, setEditedTitle] = useState(task.title)
    const [isUpdating, setIsUpdating] = useState(false)
    const [isTogglingComplete, setIsTogglingComplete] = useState(false)
    const [isCompleteIconHovered, setIsCompleteIconHovered] = useState(false)
    const [showCompletionCelebration, setShowCompletionCelebration] =
        useState(false)
    const [celebrationTaskId, setCelebrationTaskId] = useState(null)
    const [parentEpicModalOpen, setParentEpicModalOpen] = useState(false)
    const inputRef = useRef(null)
    const subTaskAccordionRef = useRef(null)
    const celebrationTimeoutRef = useRef(null)
    const taskIdRef = useRef(task?._id)
    taskIdRef.current = task?._id
    const COMPLETION_CELEBRATION_MS = 2600

    const clearCompletionCelebration = useCallback(() => {
        if (celebrationTimeoutRef.current) {
            clearTimeout(celebrationTimeoutRef.current)
            celebrationTimeoutRef.current = null
        }
        setCelebrationTaskId(null)
        setShowCompletionCelebration(false)
    }, [])

    const triggerCompletionCelebration = useCallback((forTaskId) => {
        if (!forTaskId) return
        if (celebrationTimeoutRef.current) {
            clearTimeout(celebrationTimeoutRef.current)
            celebrationTimeoutRef.current = null
        }
        setCelebrationTaskId(forTaskId)
        setShowCompletionCelebration(true)
        celebrationTimeoutRef.current = setTimeout(() => {
            setCelebrationTaskId(null)
            setShowCompletionCelebration(false)
            celebrationTimeoutRef.current = null
        }, COMPLETION_CELEBRATION_MS)
    }, [])

    useEffect(() => {
        clearCompletionCelebration()
    }, [task?._id, clearCompletionCelebration])

    useEffect(
        () => () => {
            clearCompletionCelebration()
        },
        [clearCompletionCelebration]
    )

    const getInitials = (name) => {
        if (!name) return 'U'
        const parts = name.split(' ')
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase()
        }
        return name.substring(0, 2).toUpperCase()
    }

    const getAssigneeColor = (name) => {
        if (!name) return '#e2e8f0'
        const colors = [
            '#85BBF9',
            '#6366f1',
            '#8b5cf6',
            '#ec4899',
            '#f59e0b',
            '#10b981',
            '#06b6d4',
        ]
        const index = name.charCodeAt(0) % colors.length
        return colors[index]
    }

    const handleEditClick = (e) => {
        e.stopPropagation()
        if (isPendingCreate) return
        setIsEditing(true)
        setEditedTitle(task.title)
        setTimeout(() => {
            if (inputRef.current) {
                const textArea =
                    inputRef.current.resizableTextArea?.textArea ||
                    inputRef.current
                textArea.focus()
                if (textArea.select) {
                    textArea.select()
                } else if (textArea.setSelectionRange) {
                    textArea.setSelectionRange(0, textArea.value.length)
                }
            }
        }, 0)
    }

    /**
     * Leaving edit mode drops back to the title held in the list, so without
     * this the card re-renders the pre-edit text until refreshTasks() has
     * round-tripped — the old title visibly reappears for a second or two.
     * Patch locally first, exactly as the assignee/epic/date handlers do.
     */
    const patchLocalTaskTitle = useCallback(
        (title) => {
            setLocalTaskList((prev) =>
                Array.isArray(prev)
                    ? prev.map((group) =>
                          Array.isArray(group?.list)
                              ? {
                                    ...group,
                                    list: group.list.map((item) =>
                                        item?._id === task?._id
                                            ? { ...item, title }
                                            : item
                                    ),
                                }
                              : group
                      )
                    : prev
            )
        },
        [setLocalTaskList, task?._id]
    )

    const handleSave = async () => {
        if (isPendingCreate) return

        const previousTitle = task.title
        const nextTitle = editedTitle.trim()

        if (nextTitle === previousTitle.trim()) {
            setIsEditing(false)
            return
        }

        if (!nextTitle) {
            // Clearing the field and clicking away is a cancel, not a failure:
            // the title is restored untouched and nothing is sent, so an error
            // here just contradicts what the user sees.
            setEditedTitle(previousTitle)
            setIsEditing(false)
            return
        }

        patchLocalTaskTitle(nextTitle)

        setIsUpdating(true)
        try {
            let result
            if (isMainCard) {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    title: nextTitle,
                })
                result = await dispatch(mainTaskUpdate({ id: task._id, body }))
            } else {
                const payload = {
                    _id: task._id,
                    updateTask: {
                        title: nextTitle,
                        projectId: projectId,
                        type: task.type || 'Task',
                        assigneeId: resolveReferenceId(task.assigneeId),
                        priority: task.priority || 'Low',
                        status: task.status || 'To Do',
                    },
                }
                result = await dispatch(taskUpdateSupport(payload))
            }

            const data = result?.payload?.data
            if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                refreshTasks(0)
                setIsEditing(false)
            } else {
                patchLocalTaskTitle(previousTitle)
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update task',
                })
                setEditedTitle(previousTitle)
            }
        } catch (error) {
            patchLocalTaskTitle(previousTitle)
            showActionNotification({
                type: 'error',
                message: 'Failed to update task',
            })
            setEditedTitle(previousTitle)
        } finally {
            setIsUpdating(false)
            setIsEditing(false)
        }
    }

    const handleCancel = () => {
        setEditedTitle(task.title)
        setIsEditing(false)
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault()
            handleSave()
        } else if (e.key === 'Escape') {
            e.preventDefault()
            handleCancel()
        }
    }

    const handleAssigneeChange = useCallback(
        async (selected) => {
            if (isPendingCreate) return
            const newAssigneeId = selected?._id || selected
            const currentAssigneeId = resolveReferenceId(task.assigneeId)
            if (newAssigneeId === currentAssigneeId) {
                return
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? {
                                            ...item,
                                            assigneeId: selected
                                                ? [
                                                      {
                                                          name: selected?.name,
                                                          _id: selected?._id,
                                                          email: selected?.email,
                                                          profilePicture:
                                                              selected?.profilePicture,
                                                      },
                                                  ]
                                                : null,
                                        }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                let result
                if (isMainCard) {
                    const body = buildMainTaskUpdateBodyFromTask(
                        task,
                        projectId,
                        { assigneeId: newAssigneeId }
                    )
                    result = await dispatch(
                        mainTaskUpdate({ id: task._id, body })
                    )
                } else {
                    const payload = {
                        _id: task._id,
                        updateTask: {
                            title: task.title,
                            projectId: projectId,
                            type: task.type || 'Task',
                            assigneeId: newAssigneeId,
                            priority: task.priority || 'Low',
                            status: task.status || 'To Do',
                            description: task.description,
                            start: task.start,
                            estimation: task.estimation,
                        },
                    }
                    result = await dispatch(taskUpdateSupport(payload))
                }

                const data = result?.payload?.data
                if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                    dispatch(
                        projectTaskListAssigneeUser({
                            skip: 0,
                            limit: 100,
                            search: '',
                            projectId: projectId,
                            ...(isMainCard ? { type: 'pms' } : {}),
                        })
                    )
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update assignee',
                    })
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update assignee',
                })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const handleDateRangeChange = useCallback(
        async (dateRange) => {
            if (isPendingCreate) return
            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? {
                                            ...item,
                                            start: dateRange?.start || null,
                                            end: dateRange?.end || null,
                                        }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                let result
                if (isMainCard) {
                    const body = buildMainTaskUpdateBodyFromTask(
                        task,
                        projectId,
                        {
                            start: dateRange?.start || null,
                            end: dateRange?.end || null,
                        }
                    )
                    result = await dispatch(
                        mainTaskUpdate({ id: task._id, body })
                    )
                } else {
                    const payload = {
                        _id: task._id,
                        updateTask: {
                            title: task.title,
                            projectId: projectId,
                            type: task.type || 'Task',
                            assigneeId:
                                task.assigneeId?.[0]?._id ??
                                task.assigneeId?._id ??
                                task.assigneeId ??
                                null,
                            priority: task.priority || 'Low',
                            status: task.status || 'To Do',
                            description: task.description,
                            start: dateRange?.start || null,
                            end: dateRange?.end || null,
                            estimation: task.estimation,
                        },
                    }
                    result = await dispatch(taskUpdateSupport(payload))
                }

                const data = result?.payload?.data
                if (isMainCard ? isTaskMutationSuccess(data) : data?.success) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update date',
                    })
                }
            } catch (error) {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update date',
                })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const handleTypeChange = useCallback(
        async (_name, nextType) => {
            if (!isMainCard || readOnlyActions || isPendingCreate) return
            const typeValue = nextType || 'Task'
            if (
                String(typeValue).toLowerCase() ===
                String(taskTypeLabel).toLowerCase()
            ) {
                return
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? { ...item, type: typeValue }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    type: typeValue,
                })
                const result = await dispatch(
                    mainTaskUpdate({ id: task._id, body })
                )
                const data = result?.payload?.data
                if (isTaskMutationSuccess(data)) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update type',
                    })
                }
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update type',
                })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            task,
            projectId,
            dispatch,
            setLocalTaskList,
            isMainCard,
            readOnlyActions,
            refreshTasks,
            taskTypeLabel,
        ]
    )

    const handleEpicChange = useCallback(
        async (epicId) => {
            if (!isMainCard || isPendingCreate) return false
            const currentEpicId = getTaskEpicId(task)
            if (String(epicId ?? '') === String(currentEpicId ?? '')) {
                return true
            }

            setLocalTaskList((prev) =>
                prev.map((group) =>
                    Array.isArray(group?.list)
                        ? {
                              ...group,
                              list: group.list.map((item) =>
                                  item?._id === task?._id
                                      ? { ...item, epic: epicId, epicId }
                                      : item
                              ),
                          }
                        : group
                )
            )

            try {
                const body = buildMainTaskUpdateBodyFromTask(task, projectId, {
                    epic: epicId,
                })
                const result = await dispatch(
                    mainTaskUpdate({ id: task._id, body })
                )
                const data = result?.payload?.data
                if (isTaskMutationSuccess(data)) {
                    refreshTasks(0)
                } else {
                    showActionNotification({
                        type: 'error',
                        message:
                            result?.payload?.data?.message ||
                            'Failed to update epic',
                    })
                }
            } catch {
                showActionNotification({
                    type: 'error',
                    message: 'Failed to update epic',
                })
            }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [task, projectId, dispatch, setLocalTaskList, isMainCard, refreshTasks]
    )

    const performSupportCompleteToggle = useCallback(async () => {
        if (
            isMainCard ||
            isTogglingComplete ||
            isPendingCreate ||
            readOnlyActions
        )
            return

        const completedTaskId = task?._id
        const nextCompleted = !Boolean(task?.isCompleted ?? task?.isComplete)

        if (nextCompleted) {
            triggerCompletionCelebration(completedTaskId)
        } else {
            clearCompletionCelebration()
        }

        setIsTogglingComplete(true)
        setLocalTaskList((prev) =>
            prev.map((group) =>
                Array.isArray(group?.list)
                    ? {
                          ...group,
                          list: group.list.map((item) =>
                              item?._id === task?._id
                                  ? { ...item, isCompleted: nextCompleted }
                                  : item
                          ),
                      }
                    : group
            )
        )
        try {
            const payload = {
                _id: task._id,
                updateTask: {
                    title: task.title,
                    projectId: projectId,
                    type: task.type || 'Task',
                    assigneeId:
                        task.assigneeId?.[0]?._id ??
                        task.assigneeId?._id ??
                        task.assigneeId ??
                        null,
                    priority: task.priority || 'Low',
                    status: task.status || 'To Do',
                    description: task.description,
                    start: task.start,
                    end: task.end,
                    estimation: task.estimation,
                    isCompleted: nextCompleted,
                },
            }

            const result = await dispatch(taskUpdateSupport(payload))
            const data = result?.payload?.data
            if (data?.success) {
                if (
                    String(taskIdRef.current || '') !==
                    String(completedTaskId || '')
                ) {
                    clearCompletionCelebration()
                }
                refreshTasks(0)
            } else {
                clearCompletionCelebration()
                showActionNotification({
                    type: 'error',
                    message:
                        result?.payload?.data?.message ||
                        'Failed to update task completion',
                })
            }
        } catch (error) {
            clearCompletionCelebration()
            showActionNotification({
                type: 'error',
                message: 'Failed to update task completion',
            })
        } finally {
            setIsTogglingComplete(false)
        }
    }, [
        task,
        projectId,
        dispatch,
        isMainCard,
        isTogglingComplete,
        isPendingCreate,
        readOnlyActions,
        refreshTasks,
        setLocalTaskList,
        triggerCompletionCelebration,
        clearCompletionCelebration,
    ])

    const handleToggleSupportTaskComplete = useCallback(
        (e) => {
            e.stopPropagation()
            performSupportCompleteToggle()
        },
        [performSupportCompleteToggle]
    )

    const handleOpenTaskDetails = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate) return
            clearCompletionCelebration()
            onTaskClick(task._id)
        },
        [isPendingCreate, onTaskClick, task._id, clearCompletionCelebration]
    )

    const handleOpenTaskInNewTab = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate || !taskDeepLink) return
            clearCompletionCelebration()
            window.open(taskDeepLink, '_blank', 'noopener,noreferrer')
        },
        [isPendingCreate, taskDeepLink, clearCompletionCelebration]
    )

    const handleCopyTaskLink = useCallback(
        async (e) => {
            e?.stopPropagation?.()
            if (isPendingCreate || !taskDeepLink) return
            const ok = await copyTaskContextLink(taskDeepLink)
            showActionNotification({
                type: ok ? 'success' : 'error',
                message: ok ? 'Link Copied!' : 'Could not copy link',
                notificationKey: 'task-card-link-copied',
            })
        },
        [isPendingCreate, taskDeepLink]
    )

    const handleAddSubtaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (
                hasParentTask ||
                !canAddSubtasks ||
                readOnlyActions ||
                isPendingCreate
            )
                return
            if (
                !isMainCard &&
                subTaskAccordionRef.current?.openAddSubtaskComposer
            ) {
                subTaskAccordionRef.current.openAddSubtaskComposer()
                return
            }
            clearCompletionCelebration()
            onTaskClick(task._id, 'add-subtasks')
        },
        [
            hasParentTask,
            canAddSubtasks,
            readOnlyActions,
            isPendingCreate,
            isMainCard,
            onTaskClick,
            task._id,
            clearCompletionCelebration,
        ]
    )

    /*  const handleParentEpicFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (readOnlyActions || !isMainCard || hasParentTask) return
            setParentEpicModalOpen(true)
        },
        [readOnlyActions, isMainCard, hasParentTask]
    ) */

    const handleParentEpicModalConfirm = useCallback(
        (epicId) => handleEpicChange(epicId),
        [handleEpicChange]
    )

    const handleDeleteTaskFromMenu = useCallback(
        (e) => {
            e?.stopPropagation?.()
            if (!canDeleteModule || isPendingCreate || !task?._id) return
            confirmDeleteTask({
                type: task?.type,
                // Timelogs are a Development-only concept — never block Support.
                isTimelog: isMainCard && Boolean(task?.isTimelog),
                onConfirm: async () => {
                    try {
                        const result = await dispatch(
                            isMainCard
                                ? mainTaskDelete(task._id)
                                : taskDeleteSupport(task._id)
                        )
                        const data = result?.payload?.data
                        const okDelete = isMainCard
                            ? isTaskMutationSuccess(data)
                            : data?.success
                        if (okDelete) {
                            showActionNotification({
                                type: 'success',
                                message:
                                    data?.message ||
                                    'Task deleted successfully',
                                subMessage: task.title,
                                notificationKey: 'task-deleted-from-board',
                            })
                            refreshTasks(0)
                        } else {
                            showActionNotification({
                                type: 'error',
                                message:
                                    data?.message || 'Could not delete task',
                                notificationKey: 'task-delete-failed-board',
                            })
                        }
                    } catch {
                        showActionNotification({
                            type: 'error',
                            message: 'Could not delete task',
                            notificationKey: 'task-delete-failed-board',
                        })
                    }
                },
            })
        },
        [
            canDeleteModule,
            isPendingCreate,
            task._id,
            task.title,
            task?.type,
            task?.isTimelog,
            dispatch,
            isMainCard,
            refreshTasks,
        ]
    )

    const taskTypeSelectorNode = isMainCard ? (
        <div
            data-ignore-click
            className={styles.taskCardActionItem}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
        >
            <TypeSelector
                value={taskTypeLabel}
                placeholder="Select type"
                typeOptions={TASK_TYPE_OPTIONS}
                name="type"
                compact
                disabled={
                    readOnlyActions ||
                    isPendingCreate ||
                    isTypeDisabledForIssuesSprint
                }
                updateDetails={handleTypeChange}
            />
        </div>
    ) : null

    const taskKeyNode = hasTaskKey ? (
        <Tooltip title={task.taskKey} placement="top">
            <span className={styles.taskCardKey}>{task.taskKey}</span>
        </Tooltip>
    ) : null

    return (
        <>
            <Dropdown
                trigger={isPendingCreate ? [] : ['contextMenu']}
                placement="bottomLeft"
                overlayClassName={styles.taskContextDropdown}
                popupRender={() => (
                    <div className={styles.taskContextMenu}>
                        {((!hasParentTask && canAddSubtasks) || !isMainCard) &&
                            !readOnlyActions && (
                                <div className={styles.taskContextMenuSection}>
                                    {!isMainCard && (
                                        <button
                                            type="button"
                                            className={
                                                styles.taskContextMenuItem
                                            }
                                            onClick={(ev) => {
                                                ev.stopPropagation()
                                                performSupportCompleteToggle()
                                            }}
                                            disabled={isTogglingComplete}
                                        >
                                            <Icon
                                                icon={
                                                    'mdi:check-circle-outline'
                                                }
                                                width={16}
                                                height={16}
                                                style={{
                                                    color: '#757677',
                                                    marginRight: 8,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span>
                                                {isTaskCompleted
                                                    ? 'Mark incomplete'
                                                    : 'Mark complete'}
                                            </span>
                                        </button>
                                    )}
                                    {!hasParentTask && canAddSubtasks && (
                                        <button
                                            type="button"
                                            className={
                                                styles.taskContextMenuItem
                                            }
                                            onClick={handleAddSubtaskFromMenu}
                                        >
                                            <Icon
                                                icon="cuida:subtask-outline"
                                                width={16}
                                                height={16}
                                                style={{
                                                    color: '#757677',
                                                    marginRight: 8,
                                                    flexShrink: 0,
                                                }}
                                            />
                                            <span>Add subtask</span>
                                        </button>
                                    )}
                                    {/* {isMainCard && !hasParentTask && (
                                <button
                                    type="button"
                                    className={styles.taskContextMenuItem}
                                    onClick={handleParentEpicFromMenu}
                                >
                                    <Icon
                                        icon={
                                            taskEpicId
                                                ? 'mdi:swap-horizontal'
                                                : 'mdi:folder-plus-outline'
                                        }
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#757677',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>
                                        {taskEpicId
                                            ? 'Change parent'
                                            : 'Add parent'}
                                    </span>
                                </button>
                            )} */}
                                </div>
                            )}
                        <div className={styles.taskContextMenuSection}>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleOpenTaskDetails}
                            >
                                <Icon
                                    icon="si:eye-line"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Open task details</span>
                            </button>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleOpenTaskInNewTab}
                            >
                                <Icon
                                    icon="fluent:open-48-filled"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Open in new tab</span>
                            </button>
                            <button
                                type="button"
                                className={styles.taskContextMenuItem}
                                onClick={handleCopyTaskLink}
                            >
                                <Icon
                                    icon="mdi:link-variant"
                                    width={16}
                                    height={16}
                                    style={{
                                        color: '#757677',
                                        marginRight: 8,
                                        flexShrink: 0,
                                    }}
                                />
                                <span>Copy task link</span>
                            </button>
                        </div>

                        {canDeleteModule && (
                            <div className={styles.taskContextMenuSection}>
                                <button
                                    type="button"
                                    className={`${styles.taskContextMenuItem} ${styles.taskContextMenuItemDanger}`}
                                    onClick={handleDeleteTaskFromMenu}
                                >
                                    <Icon
                                        icon="fluent:delete-20-regular"
                                        width={16}
                                        height={16}
                                        style={{
                                            color: '#dc2626',
                                            marginRight: 8,
                                            flexShrink: 0,
                                        }}
                                    />
                                    <span>
                                        {getDeleteTaskMenuLabel(task?.type)}
                                    </span>
                                </button>
                            </div>
                        )}
                    </div>
                )}
            >
                <div
                    className={`${styles.taskCardNew} ${containerClassName} ${
                        isPendingCreate ? styles.taskCardPendingCreate : ''
                    }`.trim()}
                    style={{
                        opacity: isPendingCreate
                            ? 0.72
                            : isTaskCompleted
                              ? 0.7
                              : 1,
                    }}
                    onContextMenu={(e) => e.preventDefault()}
                    onClick={(e) => {
                        if (isPendingCreate) return

                        const shouldIgnore =
                            e.target.closest('[data-ignore-click]') ||
                            e.target.closest('.ignore-view-task') ||
                            e.target.closest('.taskCardEditIcon') ||
                            e.target.closest('.taskCardLikeIcon') ||
                            e.target.closest('.taskCardCalendarIcon') ||
                            e.target.closest('.taskCardActionsRow') ||
                            e.target.closest('.ant-popover') ||
                            e.target.tagName === 'svg' ||
                            e.target.tagName === 'path'

                        if (!shouldIgnore) {
                            clearCompletionCelebration()
                            onTaskClick(task._id)
                        }
                    }}
                >
                    <Row
                        justify={'space-between'}
                        className={styles.taskCardRow}
                    >
                        <Row align={'middle'} style={{ gap: '8px', flex: 1 }}>
                            {!isMainCard && (
                                <span
                                    data-ignore-click
                                    onClick={
                                        isCardInteractive
                                            ? handleToggleSupportTaskComplete
                                            : undefined
                                    }
                                    onMouseEnter={
                                        isCardInteractive
                                            ? () =>
                                                  setIsCompleteIconHovered(true)
                                            : undefined
                                    }
                                    onMouseLeave={
                                        isCardInteractive
                                            ? () =>
                                                  setIsCompleteIconHovered(
                                                      false
                                                  )
                                            : undefined
                                    }
                                    style={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: 18,
                                        height: 18,
                                        borderRadius: '50%',
                                        border: `1px solid ${
                                            isTaskCompleted
                                                ? 'transparent'
                                                : isCompleteIconHovered
                                                  ? '#5da182'
                                                  : '#cbd5e1'
                                        }`,
                                        backgroundColor: isTaskCompleted
                                            ? '#5da182'
                                            : isCompleteIconHovered
                                              ? '#f0fdf4'
                                              : 'transparent',
                                        cursor: !isCardInteractive
                                            ? 'default'
                                            : isTogglingComplete
                                              ? 'not-allowed'
                                              : 'pointer',
                                        opacity: isTogglingComplete ? 0.6 : 1,
                                        transition:
                                            'all 150ms ease, border-color 150ms ease, background-color 150ms ease',
                                    }}
                                >
                                    <Icon
                                        icon={'iconamoon:check-light'}
                                        width={13}
                                        height={13}
                                        style={{
                                            color: isTaskCompleted
                                                ? '#ffffff'
                                                : isCompleteIconHovered
                                                  ? '#5da182'
                                                  : '#94a3b8',
                                            transition: 'color 150ms ease',
                                        }}
                                    />
                                </span>
                            )}
                            {isEditing ? (
                                singleLineTitle ? (
                                    <TextArea
                                        ref={inputRef}
                                        value={editedTitle}
                                        onChange={(e) =>
                                            setEditedTitle(e.target.value)
                                        }
                                        onBlur={handleSave}
                                        onKeyDown={handleKeyDown}
                                        className={`${styles.taskCardTitleInput} ${styles.taskCardTitleInputScrollable}`}
                                        disabled={isUpdating}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        autoSize={false}
                                        rows={1}
                                        bordered={false}
                                    />
                                ) : (
                                    <TextArea
                                        ref={inputRef}
                                        value={editedTitle}
                                        onChange={(e) =>
                                            setEditedTitle(e.target.value)
                                        }
                                        onBlur={handleSave}
                                        onKeyDown={handleKeyDown}
                                        className={styles.taskCardTitleInput}
                                        disabled={isUpdating}
                                        onClick={(e) => e.stopPropagation()}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        autoSize={{ minRows: 1, maxRows: 3 }}
                                        bordered={false}
                                    />
                                )
                            ) : (
                                <span
                                    className={styles.taskCardTitle}
                                    title={task.title}
                                    style={
                                        singleLineTitle
                                            ? {
                                                  display: 'block',
                                                  whiteSpace: 'nowrap',
                                                  overflow: 'hidden',
                                                  textOverflow: 'ellipsis',
                                              }
                                            : undefined
                                    }
                                >
                                    {task.title}
                                </span>
                            )}
                        </Row>
                        {!isEditing && isCardInteractive && (
                            <Tooltip title="Edit" placement="top">
                                <div
                                    className={`taskCardEditIcon ${styles.taskCardEditIcon}`}
                                    data-ignore-click
                                    onClick={handleEditClick}
                                    style={{
                                        cursor: 'pointer',
                                    }}
                                >
                                    <div className={styles.taskCardIconCircle}>
                                        <Icon
                                            icon="clarity:pencil-line"
                                            width={16}
                                            height={16}
                                            style={{ color: '#636363' }}
                                        />
                                    </div>
                                </div>
                            </Tooltip>
                        )}
                    </Row>

                    {/*
                     * Layout: flex-wrap row.
                     * - Left Row has flex:1 so it fills all leftover space and
                     *   naturally pushes the right-side items to the far edge.
                     * - Comment chip and accordionHeader sit side-by-side on the
                     *   right as plain flex siblings — NO wrapper div around them.
                     * - TaskSubTaskAccordion renders a Fragment whose second child
                     *   (accordionContent) has width:100% and wraps onto its own
                     *   flex line, spanning the full card width. Wrapping the
                     *   accordion in any container div would break that behaviour.
                     */}
                    {(showTypeInMetadataRow ||
                        showTaskEpic ||
                        showKeyInMetadataRow) && (
                        <Row
                            align="middle"
                            className={`${styles.taskCardRow} ${styles.taskCardEpicRow}`}
                            style={{ gap: '10px' }}
                        >
                            {showTypeInMetadataRow
                                ? taskTypeSelectorNode
                                : null}
                            {showKeyInMetadataRow ? taskKeyNode : null}
                            {showTaskEpic ? (
                                <div
                                    data-ignore-click
                                    className={`${styles.taskCardActionItem} ${styles.taskCardEpicWrap}`}
                                    onClick={(e) => e.stopPropagation()}
                                    onMouseDown={(e) => e.stopPropagation()}
                                >
                                    <EpicSelector
                                        projectId={projectId}
                                        value={taskEpicId}
                                        displayOption={taskEpicDisplayOption}
                                        onChange={
                                            isCardInteractive
                                                ? handleEpicChange
                                                : undefined
                                        }
                                        disabled={!isCardInteractive}
                                        compact
                                        placeholder="Select epic"
                                        className={styles.taskCardEpicTrigger}
                                    />
                                </div>
                            ) : null}
                        </Row>
                    )}
                    <Row
                        align={'middle'}
                        className={styles.taskCardRow}
                        style={{ marginTop: '12px', gap: '6px' }}
                    >
                        <Row align={'middle'} style={{ gap: '6px', flex: 1 }}>
                            <div
                                data-ignore-click
                                className={styles.taskCardActionItem}
                                style={
                                    !isCardInteractive
                                        ? { pointerEvents: 'none' }
                                        : undefined
                                }
                            >
                                <AssigneeSelectorButton
                                    selectedAssignee={
                                        selectedAssigneeForDisplay
                                    }
                                    onSelect={
                                        isCardInteractive
                                            ? handleAssigneeChange
                                            : undefined
                                    }
                                    projectId={projectId}
                                    filteredAssignees={filteredAssignees}
                                    getAssigneeColor={getAssigneeColor}
                                    getInitials={getInitials}
                                    taskListKind={taskListKind}
                                />
                            </div>
                            <div
                                data-ignore-click
                                className={`${styles.taskCardActionItem} ${
                                    styles.taskCardDatePicker
                                } ${
                                    task?.start || task?.end
                                        ? styles.taskCardDatePickerVisible
                                        : ''
                                }`}
                                style={
                                    !isCardInteractive
                                        ? { pointerEvents: 'none' }
                                        : undefined
                                }
                            >
                                <DateRangePicker
                                    value={{
                                        start: task?.start || null,
                                        end: task?.end || null,
                                    }}
                                    onChange={
                                        isCardInteractive
                                            ? handleDateRangeChange
                                            : undefined
                                    }
                                    placeholder=""
                                    highlightOverdue
                                />
                            </div>
                        </Row>
                        {(task?.commentsLength > 0 || attachmentCount > 0) && (
                            <div className={styles.taskMetaGroup}>
                                {task?.commentsLength > 0 && (
                                    <Tooltip
                                        title="View comments"
                                        placement="top"
                                    >
                                        <div
                                            data-ignore-click
                                            className={styles.taskMeta}
                                            style={{
                                                cursor: isPendingCreate
                                                    ? 'default'
                                                    : 'pointer',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (isPendingCreate) return
                                                onTaskClick?.(
                                                    task._id,
                                                    'comments'
                                                )
                                            }}
                                        >
                                            <span
                                                className={styles.commentCount}
                                            >
                                                {task?.commentsLength}
                                            </span>
                                            <Icon
                                                icon="iconamoon:comment-light"
                                                width={14}
                                                height={14}
                                                className={styles.commentIcon}
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                                {attachmentCount > 0 && (
                                    <Tooltip
                                        title="View attachments"
                                        placement="top"
                                    >
                                        <div
                                            data-ignore-click
                                            className={styles.taskMeta}
                                            style={{
                                                cursor: isPendingCreate
                                                    ? 'default'
                                                    : 'pointer',
                                            }}
                                            onClick={(e) => {
                                                e.stopPropagation()
                                                if (isPendingCreate) return
                                                onTaskClick?.(
                                                    task._id,
                                                    'attachments'
                                                )
                                            }}
                                        >
                                            <span
                                                className={styles.commentCount}
                                            >
                                                {attachmentCount}
                                            </span>
                                            <Icon
                                                icon="mdi:paperclip"
                                                width={14}
                                                height={14}
                                                className={styles.commentIcon}
                                            />
                                        </div>
                                    </Tooltip>
                                )}
                            </div>
                        )}
                        {!isPendingCreate && (
                            <TaskSubTaskAccordion
                                ref={subTaskAccordionRef}
                                taskId={task._id}
                                projectId={projectId}
                                filteredAssignees={filteredAssignees}
                                getAssigneeColor={getAssigneeColor}
                                getInitials={getInitials}
                                parentTask={task}
                                onTaskUpdate={() => refreshTasks(0)}
                                onTaskClick={onTaskClick}
                                taskListKind={taskListKind}
                                readOnly={readOnlyActions}
                                listQuery={listQuery}
                                openInTaskDetail={taskRouteView === 'sprint'}
                            />
                        )}
                    </Row>
                    {showCompletionCelebration &&
                        celebrationTaskId &&
                        String(celebrationTaskId) === String(task?._id) && (
                            <>
                                {typeof document !== 'undefined' &&
                                    createPortal(
                                        <div
                                            key={`task-complete-fx-${celebrationTaskId}`}
                                            className={
                                                styles.taskCompleteUnicornFx
                                            }
                                            onAnimationEnd={
                                                clearCompletionCelebration
                                            }
                                        >
                                            <img
                                                className={
                                                    styles.taskCompleteUnicornSvg
                                                }
                                                src={unicornFxImage}
                                                alt="Task completed celebration"
                                                aria-hidden="true"
                                            />
                                        </div>,
                                        document.body
                                    )}
                            </>
                        )}
                </div>
            </Dropdown>
            {isMainCard && !readOnlyActions && parentEpicModalOpen ? (
                <TaskParentEpicModal
                    open
                    mode={taskEpicId ? 'change' : 'add'}
                    projectId={projectId}
                    initialEpicId={taskEpicId}
                    taskTitle={task?.title}
                    onCancel={() => setParentEpicModalOpen(false)}
                    onConfirm={handleParentEpicModalConfirm}
                />
            ) : null}
        </>
    )
}

/**
 * Boards render hundreds of these, and each one carries antd Dropdown/Popover
 * overlays plus the subtask accordion. Without memo every unrelated board state
 * change (column hover, section menu, create-task card, drag) re-rendered the
 * whole card tree, which is what made the development board feel laggy.
 */
export const TaskCard = React.memo(TaskCardComponent)
TaskCard.displayName = 'TaskCard'

export default ProjectTaskList
